/* =============================================================
   KCH Custom JS, Kitty Cat Habitat
   Loaded from layout/theme.liquid after theme.js so the Concept
   accordion engine (accordion-details) is defined first.
   ============================================================= */
(() => {
  'use strict';
  /* Run-once guard: theme editor re-injection and instant-page
     navigations must not stack document-level listeners. */
  if (window.kchCustomInit) return;
  window.kchCustomInit = true;
  /* Drawer swipe-close, transition event name. theme.js ModalElement.onPanEnd (line 2196)
     waits for whichTransitionEvent(), which returns the legacy "webkitTransitionEnd".
     Measured 22 Jul 2026: Chromium 141 and WebKit 26 both still dispatch the prefixed alias
     when it is the only listener registered, so the premise an earlier version of this
     comment stated, that current Chromium does not fire it, was wrong. The patch stays
     because the alias is legacy and an engine dropping it would silently break the fallback
     close path on every drawer at once. theme.js untouched.
     Target corrected 22 Jul 2026. 'cart-drawer' and 'search-drawer' are real elements, but
     they are defined in cart.js line 43 and search.js line 4, both loading conditionally
     after theme.js, so waiting on them tied the patch to which assets a template happens to
     load. modal-element is registered unconditionally in theme.js line 2233, ModalElement
     owns whichTransitionEvent (theme.js 2215), and CartDrawer, SearchDrawer, MenuDrawer and
     QuickView all inherit it, so one patch reaches every drawer. */
  (() => {
    const realTransitionEndEvent = () => {
      const el = document.createElement('div');
      if ('ontransitionend' in el || el.style.transition !== undefined) {
        return 'transitionend';
      }
      if (el.style.WebkitTransition !== undefined) return 'webkitTransitionEnd';
      if (el.style.OTransition !== undefined) return 'oTransitionEnd';
      return 'transitionend';
    };
    const patchWhichTransitionEvent = (ctorName) => {
      const Ctor = window.customElements && customElements.get
        ? customElements.get(ctorName)
        : null;
      if (!Ctor || !Ctor.prototype) return false;
      /* Patch the prototype that owns whichTransitionEvent (ModalElement), so every
         drawer that inherits it is fixed, not one subclass. */
      let proto = Ctor.prototype;
      while (proto && !Object.prototype.hasOwnProperty.call(proto, 'whichTransitionEvent')) {
        proto = Object.getPrototypeOf(proto);
      }
      if (!proto || proto.__kchTransitionEventPatched) return false;
      const corrected = realTransitionEndEvent();
      proto.whichTransitionEvent = function () { return corrected; };
      proto.__kchTransitionEventPatched = true;
      return true;
    };
    /* modal-element first, the owner; the rest are fallbacks against a future rename. */
    const kchDrawers = ['modal-element', 'drawer-element', 'x-modal'];
    const tryPatch = () => {
      for (let i = 0; i < kchDrawers.length; i++) {
        if (patchWhichTransitionEvent(kchDrawers[i])) return true;
      }
      return false;
    };
    if (window.customElements && customElements.whenDefined) {
      kchDrawers.forEach((name) => {
        customElements.whenDefined(name).then(tryPatch).catch(() => {});
      });
    }
    /* Cover the already-defined case immediately too; idempotent via the proto flag. */
    tryPatch();
  })();
  /* Drawer drag-to-close, reach and completion. Two faults in ModalElement.onPanEnd
     (theme.js 2175 to 2204), both of which leave a drag looking like it did nothing.
     Reach. The close threshold is 30% of the sheet height. On a 956pt phone the sheet is
     896px, so the drag has to travel 269px, started from a 40px grab strip. Anything shorter
     springs back. That is most attempts.
     Completion. Past the threshold, theme.js does not close the drawer directly. It sets a
     transform and waits for a one-shot listener on whichTransitionEvent(), which returns the
     legacy "webkitTransitionEnd". If that event does not arrive the drawer stays open with
     the inline transform still on it.
     This replaces the decision for drags of 15% or more and closes on a timer instead of a
     transition event, so the outcome does not depend on which name the engine dispatches.
     Shorter drags fall through to theme.js untouched, so a tap or a nudge still springs back
     exactly as before. hide() is the theme's own close path and calls resetGesture() first,
     so no inline transform is ever left behind.
     Deliberately narrow: it only runs inside a real panend, which only fires from a touchend
     on the gesture element. It never runs on a cancelled or spurious touch. */
  (() => {
    const CLOSE_FRACTION = 0.15;
    const SETTLE_MS = 220;
    const patchPanEnd = () => {
      const Ctor = window.customElements && customElements.get
        ? customElements.get('modal-element')
        : null;
      if (!Ctor || !Ctor.prototype) return false;
      let proto = Ctor.prototype;
      while (proto && !Object.prototype.hasOwnProperty.call(proto, 'onPanEnd')) {
        proto = Object.getPrototypeOf(proto);
      }
      if (!proto || proto.__kchPanEndPatched) return false;
      const original = proto.onPanEnd;
      proto.onPanEnd = function () {
        const gesture = this.gesture;
        const moveY = gesture ? gesture.touchMoveY : null;
        const height = this.gestureConfig ? this.gestureConfig.layerHeight : null;
        if (this.hasAttribute('open') && height && moveY != null && moveY >= height * CLOSE_FRACTION) {
          if (this.gestureConfig.animationFrame !== null) {
            cancelAnimationFrame(this.gestureConfig.animationFrame);
            this.gestureConfig.animationFrame = null;
          }
          /* The exact transform written here doubles as the settle token. Every hide path
             runs resetGesture(), which clears it, so if anything closes and reopens this
             drawer inside the settle window the timer finds a different value and stands
             down instead of hiding the fresh open. Found by test: without this check a
             drag-close raced against an immediate reopen killed the reopened drawer. */
          const settleTransform = 'translateY(' + height + 'px)';
          this.gestureWrap.style.transition = 'transform .2s linear';
          this.gestureWrap.style.transform = settleTransform;
          setTimeout(() => {
            if (this.hasAttribute('open') && this.gestureWrap.style.transform === settleTransform) {
              this.hide();
            }
          }, SETTLE_MS);
          return;
        }
        return original.apply(this, arguments);
      };
      proto.__kchPanEndPatched = true;
      return true;
    };
    if (window.customElements && customElements.whenDefined) {
      customElements.whenDefined('modal-element').then(patchPanEnd).catch(() => {});
    }
    patchPanEnd();
  })();
  /* Cancelled drags, visual reset only. GestureElement binds touchstart, touchmove and
     touchend (theme.js 1793 to 1795) and nothing for touchcancel, so a drag the system takes
     over leaves the inline transform and overlay opacity written by the last panmove sitting
     on the drawer: open, offset down the screen, overlay half faded.
     This clears those four inline properties and nothing else. It does not dispatch a
     gesture event, does not call hide(), and does not touch the modal's open or active
     attributes, so it cannot put the drawer's state out of step with what is on screen. An
     earlier version of this block did synthesise a panend, which drove theme.js with a stale
     touchMoveY and stopped drawers closing. That approach is not repeated here. */
  (() => {
    document.addEventListener('touchcancel', (event) => {
      const target = event.target;
      if (!target || typeof target.closest !== 'function') return;
      const gesture = target.closest('gesture-element');
      if (!gesture) return;
      clearTimeout(gesture.longPressTimer ?? undefined);
      const wrap = gesture.parentElement;
      if (wrap && wrap.style.transform) {
        wrap.style.transition = 'transform .2s linear';
        wrap.style.transform = 'translateY(0)';
      }
      const modal = gesture.closest('[open]');
      const overlay = modal ? modal.querySelector('.fixed-modal') : null;
      if (overlay) {
        overlay.style.transition = '';
        overlay.style.opacity = '';
      }
    }, true);
  })();
  /* FAQ accordion behaviour: one item open at a time, and a jump that brings the opened
     question under the header. FAQ groups (.faqs .faq) ONLY. Driven by Concept's
     bubbling 'toggle' CustomEvent.
     22 Aug 2026: the FAQ auto-open was removed from kchOpenFirst, see the note there.
     Which FAQ row arrives open is now the question block's own "Open row by default"
     checkbox in sections/faq.liquid, rendered by the server, so nothing expands after
     paint. That section also enforces one open row per group at render time, so the
     one-at-a-time rule below is no longer the only thing holding it: this handler
     keeps it true after a click, the Liquid keeps it true at paint.
     PRODUCT COLLAPSIBLE ROWS: two calls, kept as history. 18 Aug 2026 they were reverted
     to stock (independent rows, no close, no auto-open, kchPinRow scroll lock deleted)
     after the pin-era behaviour misfired against the Concept demo. 19 Aug 2026, with the
     stock-pattern jump proven on the FAQs, Sean extended the FAQ behaviour to the product
     rows: one open at a time, first row opened when a group renders fully closed, and the
     jump on open when a sibling closed. The zero-open guard still leaves the block's own
     "opened" checkbox authoritative. main-product renders its rows as plain independent
     <details>, never wrapped in stock's <accordions-details> group (theme.js 1605, used
     only by sections/product-details.liquid), so this block is what supplies the
     behaviour. Footer and cart accordions match neither selector and pass through.
     Later on 19 Aug, two refinements from Sean's testing: the sticky header is held
     calm while these managed toggles reflow and jump (see the kchHeaderCalm block at
     the end of this file), and rows inside the quick view keep the one-at-a-time close
     but never fire the jump, because window scrolling behind a fixed modal moves the
     page and repositions nothing. */
  const FAQ_GROUP = '.faqs .faq';
  const ENGINE_ITEM = 'details[is="accordion-details"]';
  const PRODUCT_ITEM = 'details.product__accordion[is="accordion-details"]';
  function kchGroupItems(target) {
    const faqGroup = target.closest(FAQ_GROUP);
    if (faqGroup) {
      return [...faqGroup.querySelectorAll(ENGINE_ITEM)];
    }
    if (target.matches?.(PRODUCT_ITEM) && target.parentElement) {
      return [...target.parentElement.children].filter((el) => {
        return el.matches(PRODUCT_ITEM);
      });
    }
    return null;
  }
  /* Jump the opened row under the header, FAQs and product collapsible rows alike.
     Rewritten 18 Aug 2026, third build; extended to product rows 19 Aug 2026.
     WHY THE SECOND BUILD BOUNCED, Sean's report from the draft theme: the damped glide
     ran WHILE Concept's 0.25s collapse of the closing sibling was still reflowing the
     page. Early frames measured the summary far below target and scrolled down; the
     collapse then pulled the whole list up past the target on its own; the loop reversed
     and eased back. Down, overshoot, return: the up-then-down Sean saw. The first build
     did not bounce only because it snapped the whole distance every frame, which is the
     roughness he reported before that.
     STOCK ALREADY SOLVES THIS. theme.js, AccordionsDetails.onToggle (lines 1616-1635):
     close the siblings, then setTimeout 250ms, matching the collapse animation, then ONE
     native window.scrollTo({ behavior: 'smooth' }) to the opened item minus the header
     height, honouring theme.config.motionReduced. One motion, after the layout is done,
     which is exactly the Concept demo behaviour Sean compared against. This helper is
     that pattern, applied to the FAQ group and the product collapsible rows, with
     three KCH differences: the offset reads
     --header-height off :root plus 20px clearance (the sticky header reappears on the
     upward scroll this performs; 104 is the measured fallback), and a token stands the
     jump down if another question is clicked inside the 250ms window, plus an open check
     so a click-away never scrolls to a closed row, and the defer is 300ms rather
     than stock's 250: measured on the live FAQ, the collapse's final ~40px landed after
     the 250 mark (timer drift plus the animation starting a frame after the click),
     leaving the summary 38px above target; 300 swallows that tail and the extra 50ms is
     imperceptible. */
  let kchFaqJumpToken = 0;
  function kchScrollSummaryToTop(target) {
    const token = ++kchFaqJumpToken;
    setTimeout(() => {
      if (token !== kchFaqJumpToken) return;
      if (!target.isConnected || !target.hasAttribute('open')) return;
      if (target.closest('quick-view, .x-modal')) return;
      const summary = target.querySelector('summary');
      if (!summary) return;
      /* The glide itself is an upward scroll whenever the opened row sits above the
         reading position, so the calm must outlive it: refreshed here to cover native
         smooth scrolling's full run. */
      window.kchHeaderCalm?.(900);
      const headerVar = getComputedStyle(document.documentElement).getPropertyValue('--header-height');
      const offset = (parseFloat(headerVar) || 104) + 20;
      window.scrollTo({
        top: summary.getBoundingClientRect().top + window.scrollY - offset,
        behavior: window.theme?.config?.motionReduced ? 'auto' : 'smooth'
      });
    }, 300);
  }
  document.addEventListener('toggle', (event) => {
    const detail = event.detail;
    if (!detail || !detail.current) return;
    const target = detail.current;
      /* One open at a time: close the open sibling, only when opening. */
    let kchClosedSomething = false;
    if (detail.open) {
      const items = kchGroupItems(target);
      if (items) {
        items.forEach((item) => {
          if (item !== target && item.hasAttribute('open')) {
            item.close();
            kchClosedSomething = true;
          }
        });
      }
    }
      /* FAQ jump. Fires only when opening AND a sibling closed to make room: opening
         into empty space displaces nothing, so a scroll there would be movement the
         reader did not ask for.
         The fault this fixes, measured 18 Aug 2026, desktop 1440x900 on /pages/faq:
         open Return Policy (538px tall), read down 400px, open a short question at the
         bottom. The long answer collapses 506px, everything below rises, and scrollY
         never moves: the reader is stranded looking at whatever arrived. On a 390x844
         phone the collapse is 709px, nearly a full screen.
         Product rows joined this branch 19 Aug 2026, Sean's call once the FAQ build
         proved out: same close, same jump, same only-when-a-sibling-closed guard. The
         desktop info column is lg:sticky, so the jump was measured before extending:
         at accordion depth the summary moved 300px of a 300px window scroll (1440x900),
         so the column's sticky range is exhausted there and the rows are
         scroll-addressable on both breakpoints. Footer and cart accordions match
         neither selector and pass through untouched. */
    if (target.closest(FAQ_GROUP) || target.matches?.(PRODUCT_ITEM)) {
      /* Inside the quick view (or any fixed modal) the window is not the scroller:
         window.scrollTo moves the PAGE behind the overlay and repositions nothing in
         the modal, which is exactly the background drift Sean reported from the quick
         view's collapsible rows. The one-at-a-time close above still applies in there;
         only the jump and the header calm are page-context behaviours. */
      const kchInModal = target.closest('quick-view, .x-modal') !== null;
      if (!kchInModal) {
        /* Calm covers the whole reflow, not only the jump: a tall row closing near the
           bottom of the page shortens the document, the browser clamps scrollY upward,
           and StickyHeader reads that clamp as scrolling up and reveals the bar. 700ms
           spans Concept's 0.25s collapse plus the clamp event with margin. */
        window.kchHeaderCalm?.(700);
        if (detail.open && kchClosedSomething) kchScrollSummaryToTop(target);
      }
      return;
    }
  });
  function kchOpenFirst(root) {
    const scope = root || document;
    /* FAQ groups: branch removed 22 Aug 2026. It ran once
       customElements.whenDefined('accordion-details') resolved, roughly 1.2s past paint,
       and expanded the first row of every FAQ group the server had rendered closed.
       Measured 22 Aug on a 390x844 viewport: about 1,018px of post-paint expansion,
       CLS 0.1930 on /pages/faq and 0.1466 on /pages/shipping, both sections starting
       267px down and so fully inside the fold. Sean's call the same day: FAQ rows do
       not need to be open on arrival, and any row that should be is set in the
       customiser. The question block in sections/faq.liquid now carries an "opened"
       checkbox that emits <details ... open> server-side, which costs no shift, and
       the section renders at most one open row per group.
       Do not restore this branch: it would override that checkbox on every group where
       no row is ticked, and the shift returns with it. FAQ_GROUP and ENGINE_ITEM stay
       in use above, by kchGroupItems and the toggle handler. */
    /* Product rows, keyed by shared parent. Restored 19 Aug 2026 with the jump. */
    const parents = [];
    scope.querySelectorAll(PRODUCT_ITEM).forEach((d) => {
      if (parents.indexOf(d.parentElement) === -1) parents.push(d.parentElement);
    });
    parents.forEach((parent) => {
      const items = [...parent.children].filter((el) => {
        return el.matches(PRODUCT_ITEM);
      });
      if (!items.length) return;
      const anyOpen = items.some((d) => { return d.hasAttribute('open'); });
      if (!anyOpen) items[0].open = true;
    });
  }
  customElements.whenDefined('accordion-details').then(() => {
    kchOpenFirst(document);
  });
    /* Theme editor re-renders sections in place; re-apply on section load. */
  document.addEventListener('shopify:section:load', (event) => {
    customElements.whenDefined('accordion-details').then(() => {
      kchOpenFirst(event.target);
    });
  });
})();
/* =============================================================
   KCH Header Calm, 19 Aug 2026
   Concept's StickyHeader (theme.js 1249-1305) shows and hides the bar by comparing
   window.scrollY against this.currentScrollTop on every scroll event; any decrease
   removes 'header-hidden' (1289-1296). It has no concept of WHO scrolled, so the
   accordion jump above, and the scrollY clamp when a tall row closes near the page
   bottom, both read as the shopper scrolling up and slide the menu bar in. Sean's
   report: the bar should answer a true scroll up, not every layout shift.
   FAILED FIRST DESIGN, recorded so it is not retried: wrapping onScrollHandler on the
   prototype, the way this file's drawer patches wrap their methods. It can never
   fire. beforeInit (theme.js 1238) binds the handler at connect,
   theme.utils.throttle(this.onScrollHandler.bind(this)), so the scroll listener
   holds the ORIGINAL function captured before this file loads, and a prototype
   wrapper applied afterwards is a method nobody calls. Proven on the live page:
   calm set, upward glide, bar revealed anyway.
   WORKING DESIGN: do not intercept the vendor at all. A MutationObserver watches the
   header section's class attribute; during a calm window it snapshots whether
   'header-hidden' is present and restores that snapshot the moment the vendor flips
   it. Observer callbacks run at the microtask checkpoint after the mutation, before
   the next paint, so the bar never visibly moves. Only the DIRECTION class is
   enforced; the POSITION classes ('header-scrolled', 'header-nav-scrolled', the
   deep-scroll dropdown close) pass through, since those reflect where the page is,
   not which way it moved. The vendor's own bookkeeping (currentScrollTop) keeps
   updating untouched throughout, so the first real scroll after the calm behaves
   exactly as stock.
   Real input ends the calm immediately: wheel, touch, or a scrolling key clears the
   window and the very next scroll event is stock, so a shopper who flicks upward
   mid-glide still gets the bar. Native smooth scrolling also aborts on user input,
   so the two cancellations agree. */
(() => {
  'use strict';
  if (window.kchHeaderCalm) return;
  let calmUntil = 0;
  let armed = false;
  let snapshotHidden = null;
  let watchedNode = null;
  let expiryTimer = 0;
  const enforcer = new MutationObserver(() => {
    if (performance.now() >= calmUntil || snapshotHidden === null || !watchedNode) {
      enforcer.disconnect();
      snapshotHidden = null;
      return;
    }
    /* Restoring the class fires this observer once more; the equality check makes
       that second pass a no-op rather than a loop. */
    if (watchedNode.classList.contains('header-hidden') !== snapshotHidden) {
      watchedNode.classList.toggle('header-hidden', snapshotHidden);
    }
  });
  const stopEnforcing = () => {
    enforcer.disconnect();
    snapshotHidden = null;
    watchedNode = null;
    if (expiryTimer) { clearTimeout(expiryTimer); expiryTimer = 0; }
  };
  const startEnforcing = (ms) => {
    const node = document.querySelector('.header-section');
    if (!node) return;
    if (watchedNode !== node) {
      enforcer.disconnect();
      watchedNode = node;
      enforcer.observe(node, { attributes: true, attributeFilter: ['class'] });
    }
    if (snapshotHidden === null) {
      snapshotHidden = node.classList.contains('header-hidden');
    }
    if (expiryTimer) clearTimeout(expiryTimer);
    expiryTimer = setTimeout(stopEnforcing, ms + 60);
  };
  const clearCalm = () => {
    calmUntil = 0;
    stopEnforcing();
    if (!armed) return;
    armed = false;
    window.removeEventListener('wheel', clearCalm, true);
    window.removeEventListener('touchstart', clearCalm, true);
    window.removeEventListener('keydown', onKey, true);
  };
  const onKey = (e) => {
    const k = e.key;
    if (k === 'ArrowUp' || k === 'ArrowDown' || k === 'PageUp' || k === 'PageDown' ||
        k === 'Home' || k === 'End' || k === ' ') clearCalm();
  };
  window.kchHeaderCalm = (ms) => {
    calmUntil = performance.now() + ms;
    startEnforcing(ms);
    if (armed) return;
    armed = true;
    window.addEventListener('wheel', clearCalm, { capture: true, passive: true });
    window.addEventListener('touchstart', clearCalm, { capture: true, passive: true });
    window.addEventListener('keydown', onKey, true);
  };
})();
/* =============================================================
 KCH Dock + Lightbox additions
 1. Back-to-top handler for the mobile dock block added in
 sections/mobile-dock.liquid ([data-kch-back-to-top]).
 Replicates the theme's scroll-spy behaviour, including the
 reduced-motion preference (theme.js handleBackToTopClick).
 2. Navigation dots inside the PhotoSwipe lightbox, desktop and
 mobile. The theme builds one PhotoSwipeLightbox per
 <media-gallery> in a lazy getter (theme.js get photoswipe).
 A capture-phase click on anything inside a media-gallery
 warms that getter and attaches the uiRegister hook before the
 theme's own bubble-phase handler calls loadAndOpen, so the
 dots exist from the first open. Covers the product page and
 the quick-view drawer (same element). Dots hide themselves on
 single-image galleries. Dot aria-labels use the theme's own
 general.accessibility.go_to_item translation (the same key the
 theme uses for its gallery thumbnails), delivered as
 theme.strings.kchGoToItem by a KCH addition in
 snippets/js-variables.liquid; the English fallback below keeps
 the dots working if that snippet edit is ever lost in a theme
 update.
 ============================================================= */
(() => {
  'use strict';
  if (window.kchDockLightbox) return;
  window.kchDockLightbox = true;
  /* --- 1. Mobile dock: back to top --- */
  document.addEventListener('click', (e) => {
    const btn = e.target.closest('[data-kch-back-to-top]');
    if (!btn) return;
    const reduced = window.theme?.config?.motionReduced;
    window.scrollTo({ top: 0, behavior: reduced ? 'auto' : 'smooth' });
  });
  /* --- 2. Lightbox navigation dots --- */
  document.addEventListener('click', (e) => {
    const host = e.target.closest('media-gallery');
    if (!host || host.dataset.kchPswp) return;
    let lightbox;
    try { lightbox = host.photoswipe; } catch (err) { return; }
    if (!lightbox || typeof lightbox.on !== 'function') return;
    /* Claim the gallery only once the lightbox is confirmed; an early guard used to
       disable the hooks permanently on a single failed getter. */
    host.dataset.kchPswp = '1';
    /* Product-image shadow: toggle body.is-pswp-open from here (pairs with kch-custom.css)
       so theme.js stays stock. */
    lightbox.on('beforeOpen', () => { document.body.classList.add('is-pswp-open'); });
    lightbox.on('destroy', () => { document.body.classList.remove('is-pswp-open'); });
    lightbox.on('uiRegister', () => {
      const pswp = lightbox.pswp;
      if (!pswp || !pswp.ui) return;
      /* a11y 21 Oct 2026 (PERF-002): PhotoSwipe gives .pswp role=dialog with no name and no
         aria-modal. Its own focus handling already keeps focus inside while it is open. The
         name is the theme's products.media.gallery_viewer string (snippets/js-variables.liquid). */
      if (pswp.element) {
        pswp.element.setAttribute('aria-modal', 'true');
        pswp.element.setAttribute('aria-label', (window.theme?.strings?.kchGalleryViewer) || 'Gallery Viewer');
      }
      pswp.ui.registerElement({
        name: 'kch-dots',
        className: 'pswp__kch-dots',
        appendTo: 'wrapper',
        onInit: (el, pswpInstance) => {
          const total = pswpInstance.getNumItems();
          if (total < 2) { el.hidden = true; return; }
          const labelTpl = (window.theme?.strings?.kchGoToItem) || 'Go to item [index]';
          const dots = [];
          for (let i = 0; i < total; i++) {
            const b = document.createElement('button');
            b.type = 'button';
            b.className = 'pswp__kch-dot';
            b.setAttribute('aria-label', labelTpl.replace('[index]', String(i + 1)));
            b.addEventListener('click', (ev) => {
              ev.stopPropagation();
              pswpInstance.goTo(i);
            });
            el.appendChild(b);
            dots.push(b);
          }
          const sync = () => {
            for (let j = 0; j < dots.length; j++) {
              const active = j === pswpInstance.currIndex;
              dots[j].classList.toggle('is-active', active);
              if (active) { dots[j].setAttribute('aria-current', 'true'); }
              else { dots[j].removeAttribute('aria-current'); }
            }
          };
          pswpInstance.on('change', sync);
          sync();
        }
      });
    });
  }, true);
})();
/* Lightbox opener in the Tab order, 21 Oct 2026 (PERF-002, WCAG 2.1.1).
   Stock Concept renders every image's lightbox button with tabindex="-1", so the zoom
   view could only be opened with a mouse. snippets/product-media.liquid now leaves the
   button on the slide showing at load out of that, and this keeps exactly one opener in
   the Tab order as the slide changes (roving tabindex): the button on the current slide.
   Where the gallery is not scrolling (grid layouts at desktop widths) every image is on
   screen, so every opener is reachable.
   slider:change is dispatched by slider-element without bubbles, so it is heard here in
   the capture phase. Covers the product page and quick view (same elements). Only
   lightbox buttons are touched; the hover-zoom button stays as stock. */
(() => {
  'use strict';
  if (window.kchLightboxTab) return;
  window.kchLightboxTab = true;
  const BTN = 'button[is="media-lightbox-button"]';
  function sync(slider) {
    if (!slider || !slider.closest('media-gallery')) return;
    const buttons = slider.querySelectorAll(BTN);
    if (!buttons.length) return;
    const scrolling = slider.scrollWidth > slider.clientWidth + 1;
    const items = typeof slider.itemsToShow !== 'undefined' ? slider.itemsToShow : [];
    const current = scrolling ? (items[(slider.currentPage || 1) - 1] || items[0]) : null;
    /* slider-element not upgraded yet: leave the Liquid-rendered state as it is */
    if (scrolling && !current) return;
    buttons.forEach((b) => {
      const on = !scrolling || (current && current.contains(b));
      if (on) b.removeAttribute('tabindex');
      else b.setAttribute('tabindex', '-1');
    });
  }
  document.addEventListener('slider:change', (e) => {
    const slider = e.target;
    if (slider && slider.tagName === 'SLIDER-ELEMENT') sync(slider);
  }, true);
  const all = () => document.querySelectorAll('media-gallery slider-element').forEach(sync);
  window.addEventListener('resize', all);
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', all);
  else all();
})();
/* Quick-view gallery drag (mouse only). The gallery is a scroll-based slider-element:
   touch swipe is native but a mouse cannot drag. Adds drag-to-scroll for the mouse,
   matching the lightbox. A threshold separates drag from click, and the follow-up click
   is swallowed so it does not fire the zoom button. Native image drag is killed.
   Delegated on document because the quick view rebuilds its markup on open.
   FLAG: verify on the live theme; drag threshold may need a nudge. */
(() => {
  'use strict';
  if (window.kchQuickViewDrag) return;
  window.kchQuickViewDrag = true;
  const SEL = '.quick-view slider-element';
  const THRESHOLD = 6;
  let slider = null, startX = 0, startScroll = 0, down = false, moved = false;
  function sliderFrom(target) {
    return target && target.closest ? target.closest(SEL) : null;
  }
  document.addEventListener('pointerdown', (e) => {
    if (e.pointerType !== 'mouse' || e.button !== 0) return;
    const s = sliderFrom(e.target);
    if (!s) return;
    slider = s; down = true; moved = false;
    startX = e.clientX; startScroll = s.scrollLeft;
    /* Do not capture on press (it swallowed the lightbox click); capture once a real drag begins. */
  });
  document.addEventListener('pointermove', (e) => {
    if (!down || !slider) return;
    const dx = e.clientX - startX;
    if (!moved && Math.abs(dx) > THRESHOLD) {
      moved = true;
      try { slider.setPointerCapture(e.pointerId); } catch (err) {}
    }
    if (moved) {
      slider.scrollLeft = startScroll - dx;
      e.preventDefault();
    }
  });
  function release() {
    if (!down) return;
    down = false;
    if (moved && slider) {
      const host = slider;
      const swallow = (ev) => {
        ev.stopPropagation();
        ev.preventDefault();
        host.removeEventListener('click', swallow, true);
      };
      host.addEventListener('click', swallow, true);
      setTimeout(() => { host.removeEventListener('click', swallow, true); }, 50);
    }
    slider = null; moved = false;
  }
  document.addEventListener('pointerup', release);
  document.addEventListener('pointercancel', release);
  document.addEventListener('dragstart', (e) => {
    if (e.target?.closest?.(SEL)) e.preventDefault();
  });
  /* Media-save guard, two separate menus, two separate mechanisms.
     1. RIGHT-CLICK, both IMG and VIDEO. Product-card images are pointer-events:none in
        CSS so their right-click already hits the link menu; this closes the one desktop
        case CSS cannot without breaking zoom. VIDEO added 18 Aug 2026: the test read
        tagName === 'IMG' only, and a <video> reports 'VIDEO'.
     2. CHROME'S NATIVE CONTROLS MENU, the three-dot button in the video control bar,
        which lists Download, Playback speed and Picture in Picture. This is NOT a
        contextmenu event and preventDefault above cannot reach it. Sean's first product
        video, 17 Aug 2026, showed Download there; the contextmenu change alone did
        nothing for it, which is the fault this block was rewritten to fix.
        controlsList="nodownload" is the only lever on that menu. Shopify renders the
        product <video> with controls and no controlsList (verified on the live catnip
        tin page, 18 Aug 2026), and the markup comes from RoarThemes' own media snippet,
        so setting it here rather than in Liquid survives a Concept update.
     WHAT THIS DOES NOT DO. controlsList is Chromium only. Firefox and Safari do not
     implement it, so the download stays in their menus; the contextmenu guard above is
     what covers Safari's right-click. And none of it prevents retrieval: the file URL is
     in the page source on Shopify's CDN. This raises effort, nothing more. Watermarking
     is the only measure that survives the file being copied.
     Picture in Picture is deliberately left alone. disablePictureInPicture would remove
     it, but it is a genuine convenience and it does not hand anyone the file. */
  document.addEventListener('contextmenu', (e) => {
    const t = e.target?.tagName;
    if (t === 'IMG' || t === 'VIDEO') e.preventDefault();
  });
  /* The gallery and the quick view both inject video after load, and the quick view
     rebuilds its markup on every open, so a one-off sweep at DOMContentLoaded misses
     most of them. The data flag makes re-running free, which is what lets the observer
     fire as often as it likes. */
  const kchStripVideoDownload = (root) => {
    const scope = (root && root.querySelectorAll) ? root : document;
    scope.querySelectorAll('video:not([data-kch-cl])').forEach((v) => {
      v.setAttribute('controlsList', 'nodownload');
      v.setAttribute('data-kch-cl', '1');
    });
  };
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => kchStripVideoDownload(document));
  } else {
    kchStripVideoDownload(document);
  }
  /* Coalesced to one pass per frame: the quick view can add dozens of nodes at once. */
  (() => {
    let queued = false;
    const obs = new MutationObserver(() => {
      if (queued) return;
      queued = true;
      requestAnimationFrame(() => { queued = false; kchStripVideoDownload(document); });
    });
    try { obs.observe(document.documentElement, { childList: true, subtree: true }); } catch (e) {}
  })();
  document.addEventListener('shopify:section:load', (e) => kchStripVideoDownload(e.target));
})();
/* ============================================================================
   Search typewriter types the same phrase twice                  30 Jul 2026
   --------------------------------------------------------------------------
   The animation reads "Search for..." then immediately types "Search for..."
   again. It is not a loop. SearchTyped.init (assets/search.js) is a deliberate
   two-phase animation:
     await this.start(this.getAttribute('data-first-text'), this.startDelay);
     setTimeout(async () => {
       await this.reset();
       await this.start(this.getAttribute('data-last-text'), 0);
     }, 600);
   It types the first string, waits 600ms, erases, then types the second. The
   fault is in the markup, snippets/predictive-search.liquid line 47, where BOTH
   attributes are set from the same expression:
     data-first-text="{{ settings.typewriter_starter_text | default: search_placeholder | escape }}"
     data-last-text="{{ settings.typewriter_starter_text | default: search_placeholder | escape }}"
   RoarThemes clearly intended two different phrases, since the engine erases
   between them, but only ever wired one setting. With typewriter_starter_text
   empty both fall through to general.search.placeholder, so the same string is
   typed twice.
   Fixed here rather than in the Liquid for two reasons. predictive-search.liquid
   is a RoarThemes file, so an edit there joins the re-apply list and is lost on
   every Concept update; this file is ours and survives. And emptying
   data-last-text does NOT work: reset() runs BEFORE start(), so it erases the
   first phrase, and start('') then sets textContent to an empty string. The
   result is a blinking cursor with no placeholder at all, which is worse than
   the repeat. Verified in a harness reproducing the class logic.
   The condition is first === last, not "last is empty", so no markup change is
   needed and the patch stands down on its own if RoarThemes ever wires a real
   second string: the values differ, both methods run as stock, and the two-phase
   animation works as designed.
   start and reset are patched rather than init because init is bound in the
   constructor via Motion.inView(this, this.init.bind(this)), so a prototype
   patch applied after an element is constructed would never be seen. start and
   reset are called as this.start() and this.reset(), which resolve through the
   prototype at call time, so patching them holds whatever the load order turns
   out to be. search.js loads conditionally, so that order is not guaranteed. */
(() => {
  'use strict';
  if (window.kchTypedOnce) return;
  window.kchTypedOnce = true;
  const sameBothEnds = (el) => {
    const first = el.getAttribute('data-first-text');
    const last = el.getAttribute('data-last-text');
    return first !== null && first === last;
  };
  const patch = () => {
    const Ctor = window.customElements && customElements.get
      ? customElements.get('search-typed')
      : null;
    if (!Ctor || !Ctor.prototype || Ctor.prototype.__kchTypedOncePatched) return false;
    const proto = Ctor.prototype;
    const origStart = proto.start;
    const origReset = proto.reset;
    /* Hold the phrase and the blinking cursor rather than erasing it. */
    proto.reset = function () {
      if (sameBothEnds(this)) return Promise.resolve();
      return origReset.apply(this, arguments);
    };
    /* Do not blank textContent on the second pass. Guarded on textContent so the
       FIRST call, where textContent is still empty, always runs. */
    proto.start = function (text) {
      if (sameBothEnds(this) && this.textContent) return Promise.resolve();
      return origStart.apply(this, arguments);
    };
    proto.__kchTypedOncePatched = true;
    return true;
  };
  if (window.customElements && customElements.whenDefined) {
    customElements.whenDefined('search-typed').then(patch).catch(() => {});
  }
  patch();
})();
/* Floating newsletter bar dismiss. Two mechanisms: the pill's own dismiss is session-only
   (sessionStorage, returns next visit, no 30-day cookie); a permanent "signed up" flag
   (localStorage, set on a real signup in newsletter-popup.js) keeps it gone for good.
   Closing the popup without signing up does not hide the pill. */
(() => {
  const KEY = 'kch:newsletter-bar-closed';
  const SIGNED_UP = 'kch:newsletter-signed-up';
  const bar = document.querySelector('.newsletter-bar');
  function hideBar() { if (bar) bar.style.display = 'none'; }
  function signedUp() {
    try { return localStorage.getItem(SIGNED_UP) === '1'; } catch (e) { return false; }
  }
  function sessionClosed() {
    try { return sessionStorage.getItem(KEY) === '1'; } catch (e) { return false; }
  }
  function rememberSession() { try { sessionStorage.setItem(KEY, '1'); } catch (e) {} }
  if (bar) {
    if (sessionClosed() || signedUp()) {
      hideBar();
    } else if (bar.getAttribute('data-kch-trigger') === 'scroll') {
      // Reveal the pill only after the visitor scrolls a set proportion of the
      // page, on the allowed templates. Mirrors the back-to-top arrow's
      // proportional maths (scrollHeight - innerHeight). One reveal, then the
      // listeners detach; dismissal/subscription is handled below.
      const pages = bar.getAttribute('data-kch-pages') || 'home';
      const tmpl = bar.getAttribute('data-kch-template') || '';
      const ok = pages === 'all' ||
               (pages === 'home' && tmpl === 'index') ||
               (pages === 'home_product' && (tmpl === 'index' || tmpl === 'product'));
      if (ok) {
        const pct = parseFloat(bar.getAttribute('data-kch-scroll-pct')) || 60;
        const onScroll = () => {
          const max = (document.documentElement.scrollHeight - window.innerHeight) || 1;
          const y = window.scrollY || window.pageYOffset || 0;
          if (y >= (pct / 100) * max) {
            bar.classList.remove('kch-bar--armed');
            window.removeEventListener('scroll', onScroll);
            window.removeEventListener('resize', onScroll);
          }
        };
        window.addEventListener('scroll', onScroll, { passive: true });
        window.addEventListener('resize', onScroll);
        onScroll();
      }
    }
  }
  document.addEventListener('click', (e) => {
    if (e.target.closest('[data-newsletter-bar-close]')) {
      e.preventDefault();
      hideBar();
      rememberSession();
      return;
    }
    if (e.target.closest('.newsletter-bar__button')) {
      hideBar();
      rememberSession();
    }
  });
})();
/* Card colour swatches: swap the card photo to the chosen variant in place and point the
   card links + quick-view at it (?variant=). Desktop also swaps the hover-carousel image.
   Adding is left to quick-view / choose-options. */
(() => {
  function findImg(card) {
    let img = card.querySelector('.kch-card-img');
    if (img && img.tagName !== 'IMG') img = img.querySelector('img');
    if (!img) img = card.querySelector('.product-card__media img');
    return img;
  }
  function setSrc(img, media) {
    if (!img) return;
    img.srcset = '';
    if (img.hasAttribute('data-srcset')) img.removeAttribute('data-srcset');
    if (img.hasAttribute('data-src')) img.setAttribute('data-src', media);
    img.src = media;
  }
  function swapImg(card, media) {
    if (!media) return;
    setSrc(findImg(card), media);
    const carousel = card.querySelector('.product-card__carousel');
    if (carousel) setSrc(carousel.querySelector('.is-selected img') || carousel.querySelector('img'), media);
  }
  function retarget(card, url) {
    if (!url) return;
    const links = card.querySelectorAll('a.media, .product-card__title, .quick-add a[href]');
    for (let i = 0; i < links.length; i++) links[i].setAttribute('href', url);
    const qv = card.querySelector('.quick-view');
    if (qv) qv.setAttribute('data-product-url', url);
  }
  function setAddButton(card, variant) {
    const btn = card.querySelector('[data-kch-add-btn]');
    if (!btn) return;
    if (!btn.hasAttribute('data-kch-aria0')) btn.setAttribute('data-kch-aria0', btn.getAttribute('aria-label') || '');
    /* a11y R7-4: a button named by its own text (no aria-label in the markup) keeps that name */
    const named = btn.getAttribute('data-kch-aria0') !== '';
    /* a11y R8-2: every copy of the label: the visible one, its phone-only screen-reader copy and
       the lockstep copy in .btn-fill. querySelector alone found the .btn-fill copy (it comes
       first), which the lockstep then overwrote, so the visible text never changed. */
    const labels = btn.querySelectorAll('[data-kch-add-label]');
    const setLabel = (text) => { for (let k = 0; k < labels.length; k++) labels[k].textContent = text; };
    if (variant) {
      const idInput = card.querySelector('[data-kch-id]');
      if (idInput) idInput.value = variant.id;
      btn.classList.add('kch-can-add');
      btn.removeAttribute('aria-controls');
      if (named) btn.setAttribute('aria-label', btn.getAttribute('data-kch-add'));
      setLabel(btn.getAttribute('data-kch-add'));
    } else {
      btn.classList.remove('kch-can-add');
      const qv2 = btn.getAttribute('data-kch-qv');
      if (qv2) btn.setAttribute('aria-controls', qv2);
      if (named) btn.setAttribute('aria-label', btn.getAttribute('data-kch-aria0'));
      setLabel(btn.getAttribute('data-kch-choose'));
    }
  }
  /* KCH round 9 (22 Oct 2026): text option pills (snippets/kch-option-pills.liquid) carry one
     price template per variant; the card price follows the picked pill. Colour swatch cards have
     no templates and are unchanged. */
  function setCardPrice(card, vid) {
    if (!vid) return;
    const esc = (window.CSS && CSS.escape) ? CSS.escape(vid) : vid;
    const tpl = card.querySelector(`template[data-kch-price="${esc}"]`);
    const box = card.querySelector('[data-kch-card-price]');
    if (tpl && box) box.innerHTML = tpl.innerHTML;
  }
  function select(sw, card) {
    swapImg(card, sw.getAttribute('data-media'));
    retarget(card, sw.getAttribute('data-url'));
    const group = sw.closest('[role="radiogroup"]') || sw.closest('.swatches');
    if (group) {
      const a = group.querySelectorAll('[role="radio"], .color-swatch');
      for (let k = 0; k < a.length; k++) { a[k].classList.remove('is-active'); a[k].setAttribute('aria-checked', 'false'); a[k].setAttribute('tabindex', '-1'); }
    }
    sw.classList.add('is-active');
    sw.setAttribute('aria-checked', 'true');
    sw.setAttribute('tabindex', '0');
    card.setAttribute('data-kch-active', sw.getAttribute('data-variant-id') || '');
    setCardPrice(card, sw.getAttribute('data-variant-id'));
    if (card.querySelector('[data-kch-add-btn]')) {
      const avail = sw.getAttribute('data-available') !== 'false';
      setAddButton(card, avail ? { id: sw.getAttribute('data-variant-id') } : null);
    }
  }
  document.addEventListener('click', (e) => {
    const sw = e.target.closest('.color-swatch[data-variant-id], .kch-pill[data-variant-id]');
    if (!sw) return;
    const card = sw.closest('.product-card');
    if (!card) return;
    e.preventDefault();
    e.stopPropagation();
    if (sw.getAttribute('data-available') === 'false') return;
    select(sw, card);
  }, true);
  document.addEventListener('click', (e) => {
    const btn = e.target.closest('[data-kch-add-btn]');
    if (!btn || !btn.classList.contains('kch-can-add')) return;
    const card = btn.closest('.product-card');
    if (!card) return;
    const idInput = card.querySelector('[data-kch-id]');
    const form = idInput ? idInput.form : null;
    if (form) {
      e.preventDefault();
      e.stopPropagation();
      if (form.requestSubmit) form.requestSubmit();
      else form.dispatchEvent(new Event('submit', { cancelable: true, bubbles: true }));
    }
  }, true);
  document.addEventListener('keydown', (e) => {
    const radio = e.target.closest('[role="radio"]');
    if (!radio || !radio.closest('.product-card')) return;
    if (e.key === ' ' || e.key === 'Spacebar') {
      if (radio.tagName === 'A') { e.preventDefault(); radio.click(); }
      return;
    }
    if (e.key !== 'ArrowRight' && e.key !== 'ArrowLeft' && e.key !== 'ArrowUp' && e.key !== 'ArrowDown') return;
    const grp = radio.closest('[role="radiogroup"]');
    if (!grp) return;
    const radios = [...grp.querySelectorAll('[role="radio"]')];
    const idx = radios.indexOf(radio);
    if (idx < 0) return;
    e.preventDefault();
    /* KCH a11y 21 Oct 2026 (R4-2): Left and Right follow the reading direction (swapped in
       right-to-left), and a sold-out swatch is skipped the way a native radio group skips a
       disabled radio, so focus, tabindex 0 and aria-checked always stay on the same swatch. */
    const rtl = getComputedStyle(grp).direction === 'rtl';
    const forward = e.key === 'ArrowDown' || e.key === (rtl ? 'ArrowLeft' : 'ArrowRight');
    let next = null;
    for (let step = 1; step < radios.length; step++) {
      const cand = radios[(idx + (forward ? step : -step) + radios.length * step) % radios.length];
      if (cand.getAttribute('data-available') !== 'false') { next = cand; break; }
    }
    if (next) { next.focus(); next.click(); }
  });
  /* KCH a11y 22 Oct 2026 (R14): Home and End inside a card swatch or option group pick the first
     and last available option, as the arrows pick their neighbour. Before this the keys did nothing
     in the group and fell through to the browser, whose End scrolled the page and closed an open
     desktop mega menu, leaving focus on a hidden swatch. Capture phase plus stopPropagation keeps
     the key away from the carousel's own Home and End (kch-product-carousel.js), which would
     otherwise scroll the row while focus stays on the swatch. Only applies while focus is on a
     card radio; Home and End everywhere else are untouched. */
  document.addEventListener('keydown', (e) => {
    if (e.key !== 'Home' && e.key !== 'End') return;
    if (e.altKey || e.ctrlKey || e.metaKey || e.shiftKey) return;
    const radio = e.target.closest && e.target.closest('[role="radio"]');
    if (!radio || !radio.closest('.product-card')) return;
    const grp = radio.closest('[role="radiogroup"]');
    if (!grp) return;
    const radios = [...grp.querySelectorAll('[role="radio"]')].filter((r) => r.getAttribute('data-available') !== 'false');
    if (!radios.length) return;
    e.preventDefault();
    e.stopPropagation();
    const target = e.key === 'Home' ? radios[0] : radios[radios.length - 1];
    if (target !== radio) { target.focus(); target.click(); }
  }, true);
  (() => {
    function reapply() {
      const cards = document.querySelectorAll('.product-card[data-kch-active]');
      for (let i = 0; i < cards.length; i++) {
        const vid = cards[i].getAttribute('data-kch-active');
        if (!vid) continue;
        const esc = (window.CSS && CSS.escape) ? CSS.escape(vid) : vid;
        const sw = cards[i].querySelector(`.color-swatch[data-variant-id="${esc}"], .kch-pill[data-variant-id="${esc}"]`);
        if (sw && !sw.classList.contains('is-active')) {
          const group = sw.closest('[role="radiogroup"]') || sw.closest('.swatches');
          if (group) {
            const a = group.querySelectorAll('.color-swatch, .kch-pill');
            for (let k = 0; k < a.length; k++) { a[k].classList.remove('is-active'); a[k].setAttribute('aria-checked', 'false'); if (a[k].hasAttribute('role')) a[k].setAttribute('tabindex', '-1'); }
          }
          sw.classList.add('is-active');
          sw.setAttribute('aria-checked', 'true');
          /* KCH a11y 21 Oct 2026 (R4-2): Tab returns to the checked swatch after a re-render */
          if (sw.hasAttribute('role')) sw.setAttribute('tabindex', '0');
          setCardPrice(cards[i], vid);
        }
      }
    }
    let pending = false;
    const obs = new MutationObserver(() => {
      if (pending) return;
      pending = true;
      requestAnimationFrame(() => { pending = false; reapply(); });
    });
    const megas = document.querySelectorAll('[is="details-mega"]');
    for (let i = 0; i < megas.length; i++) {
      obs.observe(megas[i], { childList: true, subtree: true, attributes: true, attributeFilter: ['class'] });
    }
  /* Account sheet state styling, injected into Shopify's open shadow roots. The
     <shopify-account> variable API cannot express state (hover, rest-state ring), so this
     appends a stylesheet (last wins, !important beats the component). FAIL-SOFT: the class
     names are Shopify internals with no compatibility promise, so if an update renames them
     these rules stop matching and the sheet falls back to the variable-level styling in
     kch-custom.css (colours stay branded, only state styling reverts). Nothing breaks. */
  (() => {
    const SAND = 'rgb(var(--color-drawer-text))';
    const FOREST = 'rgb(var(--color-drawer-background))';
    const ACCOUNT_CSS = [
      /* Mobile backdrop: reproduce Shopify's own dialog::backdrop (black 20%) via the web
         standards <dialog>/::backdrop, not their variable names. Desktop has no backdrop, so
         this is scoped to max-width:750px. Black, not Forest: on mobile the sheet opens over
         the Forest mega menu, so Forest would dim nothing. */
      '@media (max-width:750px){',
      '  dialog::backdrop{background:rgba(0, 0, 0, 0.2)!important;}',
      '}',
      '.account__close::before,.account__close::after,',
      '.account__close > *,.account__close > *::before,.account__close > *::after{',
      '  background:none!important;background-color:transparent!important;',
      '  background-image:none!important;box-shadow:none!important;',
      '}',
      /* cursor:pointer and a cleared tap highlight, 25 Aug 2026. Sean 25 Aug: the button
         should flash to the Sand fill with a Forest glyph on press, on mobile as well as
         desktop, matching every other button in the theme. The :active rule below already
         carries that and is deliberately not behind the (hover:hover) query, but WebKit on
         iOS is known not to fire :active on a touch target unless the element is treated as
         clickable, and cursor:pointer is the usual way to signal that.
         -webkit-tap-highlight-color is cleared so iOS's own grey overlay does not compete
         with the Sand fill during the press.
         If the flash still does not appear on device, the remaining known mitigation is a
         no-op touchstart listener attached in the shadow root, which is a JS change rather
         than a CSS one and is deliberately not done here without evidence it is needed. */
      '.account__close{',
      '  cursor:pointer!important;',
      '  -webkit-tap-highlight-color:transparent!important;',
      '  background-color:transparent!important;',
      '  color:' + SAND + '!important;',
      '  box-shadow:inset 0 0 0 1px ' + SAND + '!important;',
      '  opacity:1!important;',
      '  transition:background-color var(--timing-default,.24s) ease-in-out,',
      '    color var(--timing-default,.24s) ease-in-out,',
      '    box-shadow var(--timing-default,.24s) ease-in-out!important;',
      '}',
      /* State rules, REWRITTEN AGAIN 25 Aug 2026. Third revision, and the first that removes
         the cause rather than restyling the symptom.
         What was actually wrong. Shopify's account component calls focus() on this button when
         the dialog opens. No other close button in this theme is auto-focused: .drawer__close
         and .menu-drawer .modal .close both style :focus-visible in kch-custom.css and neither
         ever shows it, because nothing focuses them. So any :focus-visible treatment on THIS
         button paints on open, every time, whatever it is.
         The build Sean supplied had :focus-visible sharing the hover rule, so it painted the
         Sand fill on open. My first revision made it transparent with a 2px outline outside
         the 1px ring, which was two rings. My second thickened the ring to 2px. All three are
         the same fault with a different symptom, and the second and third were mine.
         :focus-visible is gone from this button. Keyboard focus is driven from real modality
         instead, in wirePress() below: a Tab keydown arms it, any pointerdown disarms it, and
         the class is only applied when the button takes focus while armed. A programmatic
         focus on open therefore paints nothing, and a keyboard user still gets an indicator,
         so WCAG 2.4.7 holds. The indicator is the Sand fill, matching what every other close
         button in the theme already does on focus.
         Order: hover, then the two classes. All are (0,2,0) and (0,2,0), so source order is
         what makes a press win, which is the ordering fault that made revision two look dead
         on iOS. */
      '@media (hover:hover){',
      '  .account__close:hover{',
      '    background-color:' + SAND + '!important;',
      '    color:' + FOREST + '!important;',
      '    box-shadow:inset 0 0 0 1px ' + SAND + '!important;',
      '  }',
      '}',
      '.account__close.kch-kbfocus{',
      '  background-color:' + SAND + '!important;',
      '  color:' + FOREST + '!important;',
      '  box-shadow:inset 0 0 0 1px ' + SAND + '!important;',
      '}',
      /* Last, so a press beats a focus state at equal specificity.
         transition:none is the fix for the press never being visible, 25 Aug 2026, and it is
         the real reason the previous two revisions looked dead on device. The base rule fades
         background-color over 240ms. A tap is 50 to 100ms, and this button closes the sheet,
         so the element is gone long before the fade finishes. Measured at 390px: 4 per cent
         of the way to Sand after a 50ms press, 16 per cent after 80ms, 36 per cent after
         120ms, and only 100 per cent at 240ms. At 4 to 16 per cent over a Forest panel there
         is nothing to see, which is exactly what was reported.
         An earlier test held the press for 400ms and passed, which was not a tap and should
         not have been read as one.
         The press now snaps in at full strength. The release still fades, because the class
         comes off and the base rule's transition governs the way back, so it reads as a flash
         rather than a flicker. */
      '.account__close.kch-pressed{',
      '  transition:none!important;',
      '  background-color:' + SAND + '!important;',
      '  color:' + FOREST + '!important;',
      '  box-shadow:inset 0 0 0 1px ' + SAND + '!important;',
      '}',
      /* Only .account__close and .dialog are real Shopify internals here; the rows use
         .account__menu-region. Hover transition on the menu links. */
      '.account__menu-region a{',
      '  transition:background-color var(--animation-short,.2s cubic-bezier(.7,0,.3,1)),',
      '    color var(--animation-short,.2s cubic-bezier(.7,0,.3,1))!important;',
      '}',
      '.login-form__email-label,.login-form__email-input{font-size:0.9375rem!important;}', /* 16->15px */
      '@media (min-width:751px){',
      /* Sign-in heading: Fresh Mango (--font-heading-family) at weight 400 (its only real
         weight; anything else synthesises a faux weight). */
      '  :host,.dialog,.account{--shopify-account-font-heading:var(--font-heading-family)!important;--shopify-account-font-heading-weight:400!important;}',
        '  #dialog-heading{font-size:1.375rem!important;}', /* Heading sized up: heading too small after the "Sign in" relabel; short text has no wrap risk so increased on both breakpoints. */
      /* Menu links 15px medium (matches the email field); leading icons hidden so translated
         labels have room in the two-up desktop layout. Mobile hides its icons separately. */
      '  .account__menu-region a,.account__menu-region a *,',
      '  .account__menu-region button,.account__menu-region button *{',
      '    font-size:1rem!important;font-weight:var(--font-medium)!important;',
      '  }',
      '  .account__menu-region a svg,',
      '  .account__menu-region a img,',
      '  .account__menu-region button svg,',
      '  .account__menu-region button img,',
      '  .account__menu-region [role=\"button\"] svg,',
      '  .account__menu-region [role=\"button\"] img{',
      '    display:none!important;',
      '  }',
      '}',
      '@media (max-width:750px){',
      /* Sign-in heading: Fresh Mango at weight 400 (one real weight only). Same override as
         the desktop block above. */
      '  :host,.dialog,.account{--shopify-account-font-heading:var(--font-heading-family)!important;--shopify-account-font-heading-weight:400!important;}',
      /* Sign-in heading size, mobile only. A clamp, not a fixed px: the title sits in a flex
         row where the close and gap take width first, so it cannot use the full drawer width. */
      '  #dialog-heading{',
      '    font-size:clamp(1.25rem, calc((100vw - 56px) / 14), 1.75rem)!important;',
      '    line-height:1.1!important;',
      '  }',
      /* Menu links: medium Poppins (500 = Concept's --font-medium), mobile only. */
      '  .account__menu-region a,.account__menu-region a *,',
      '  .account__menu-region button,.account__menu-region button *{',
      '    font-size:1rem!important;font-weight:var(--font-medium)!important;',
      '    font-weight:500!important;',
      '  }',
      '}',
      '.login-form__divider,.login-form__divider *{font-size:1rem!important;}',
      /* Marketing-consent line: targeted by relation (any label/small wrapping a checkbox)
         since its class is not exposed, one step above the base size. */
      '.login-form__marketing,.login-form__marketing *,',
      '.login-form__checkbox-label,.login-form__consent,.login-form__consent *,',
      'label:has(input[type="checkbox"]),label:has(input[type="checkbox"]) *,',
      'input[type="checkbox"] ~ *,small,small *{font-size:1rem!important;}',
      /* Close button, mobile only: on the same line as the heading, 25 Aug 2026.
         REVISED. The previous build put this button at position:absolute in the top-right
         corner and reserved var(--sp-11), 44px, of padding above the heading so the two
         could not collide. That guard was written before the heading was translated and
         assumed the word could run wide enough to reach the button.
         Measured against the real header structure at 320 and 390px in all four storefront
         languages: the widest string is the French 'Se connecter', which still leaves 76px
         of clear space before the button at 320px, the narrowest width worth supporting.
         The live heading is clamp(1.25rem, calc((100vw - 56px) / 14), 1.75rem), so it
         resolves to 20px at 320px, narrower still than the 28px used in that test. The
         collision the guard existed to prevent cannot occur.
         Removing the padding alone would not work and would reintroduce the fault: an
         absolutely positioned button is out of flow, so the heading would rise underneath
         it rather than beside it. The button has to return to flow, which is what
         position:static does here.
         .account__header is display:grid in Shopify's own component, with .account__header-content
         and .account__close as its two children and no column template, so an in-flow button
         would drop to a second row. It is forced to a flex row instead, and the button is
         pushed to the end with margin-inline-start:auto, which is logical rather than
         physical so it mirrors correctly in a right-to-left locale.
         Side effect worth knowing: the header region measures 36px rather than 75px, so the
         sheet gains 39px of vertical space on a phone.
         The two rules this replaces are both gone. The padding-block-start reservation is
         the fault itself, and the *:has(> .account__close) rule existed only to neutralise
         Shopify's positioned wrapper so the absolute button could anchor to the region; with
         the button in flow it describes a behaviour that no longer exists. position:relative
         on the region is kept: it is harmless and anything else Shopify positions inside
         that subtree may still rely on it. */
      '@media (max-width:750px){',
      '  .account__header-region{position:relative;}',
      '  .account__header{',
      '    display:flex!important;flex-direction:row!important;',
      '    align-items:center!important;gap:var(--sp-3,0.75rem)!important;',
      '  }',
      '  .account__header-content{flex:1 1 auto!important;min-width:0!important;}',
      '  .account__close{',
      '    position:static!important;',
      '    flex:0 0 auto!important;',
      '    margin:0!important;margin-inline-start:auto!important;',
      /* 44px, not 36px, 25 Aug 2026. WCAG 2.2 SC 2.5.8 Target Size (Minimum) is Level AA at
         24 by 24 CSS pixels, which 36px already met, so this was never an AA failure. SC
         2.5.5 Target Size (Enhanced) is Level AAA at 44 by 44 and carries no spacing
         exception, and 36px failed that. 44px also matches Apple's Human Interface
         Guidelines and this theme's own precedent: .menu-drawer .modal .close and
         .drawer__close are both var(--sp-11) in kch-custom.css, so the account sheet was
         the outlier rather than the standard.
         Measured at 320px, the narrowest width worth supporting, against the longest string,
         the French 'Se connecter': 118px of clear space still separates the heading from the
         button, against 126px at 36px. The extra 8px costs nothing on the shared line. */
      '    width:var(--sp-11,2.75rem)!important;',
      '    height:var(--sp-11,2.75rem)!important;',
      '  }',
      /* Breathing room between the header row and the Sign in with Shop button, 25 Aug 2026.
         Sean's call: the button sits close enough under the close control that someone
         reaching for the X can land on Sign in with Shop instead, which is a destructive
         mis-tap because it starts an auth flow rather than closing a sheet.
         Shopify composes this spacing from three values, read off the live component:
           .account__header    padding-block 16px 16px
           .account__content   margin-top -32px, padding-top 32px, padding-bottom 16px
           .account__login-slot  display:contents, so it contributes no box of its own
         The negative margin and the padding cancel, so the visible gap between the heading
         row and the login form is the header's own 16px bottom padding. Adding 16px to the
         content's top padding doubles it to 32px.
         Stated as computed rather than measured. The account dialog does not lay out in a
         headless browser, every rect returning zero even with the dialog forced open, so
         the 16px is derived from the composition above and not from a rendered measurement.
         If the gap on device is not visibly doubled, this one value is the dial.
         Scoped to mobile, matching the rest of this block. Desktop spacing is untouched. */
      '  .account__content{padding-block-start:var(--sp-12,3rem)!important;}',
      /* Mobile: hide the menu-button icons (structure, not class names) and reduce button
         padding. Desktop keeps icons and stock padding (this is the max-width:750px block). */
      '  .account__menu-region a svg,',
      '  .account__menu-region a img,',
      '  .account__menu-region button svg,',
      '  .account__menu-region button img,',
      '  .account__menu-region [role=\"button\"] svg,',
      '  .account__menu-region [role=\"button\"] img{',
      '    display:none!important;',
      '  }',
      '  .account__menu-region a,',
      '  .account__menu-region button,',
      '  .account__menu-region [role=\"button\"]{',
      '    padding-block:var(--sp-2,0.5rem)!important;',
      '  }',
      '}',
      /* Consent checkbox alignment lives in LOGIN_FORM_CSS (adopted on both roots), beside
         the font rule that caused the misalignment. */
      /* Row hover: flip the wrapper too (Shopify tints .account__item-wrapper with a 12%
         Sand wash, so colouring only the anchor left it showing), and list :active for touch. */
      /* Submenu-opening item (Explore) is a button, not an anchor, so the anchor-only hover
         rule missed it and Shopify's 12% wash stayed. Match the button shapes too. */
      '.account__menu-region a:hover,.account__menu-region a:active,',
      '.account__menu-region a:focus-visible,',
      '.account__menu-region button:hover,.account__menu-region button:active,',
      '.account__menu-region button:focus-visible,',
      '.account__menu-region summary:hover,.account__menu-region summary:active,',
      '.account__menu-region summary:focus-visible,',
      '.account__menu-region [role="button"]:hover,',
      '.account__menu-region [role="button"]:active,',
      '.account__menu-region [role="button"]:focus-visible{',
      '  background-color:' + SAND + '!important;',
      '  color:' + FOREST + '!important;',
      '  opacity:1!important;',
      '}',
      /* Text only: name the text nodes, not the container, so the hover tint is not fought. */
      /* Extended to the same shapes as the rule above. */
      '.account__menu-region a:hover *,',
      '.account__menu-region a:active *,',
      '.account__menu-region a:focus-visible *,',
      '.account__menu-region button:hover *,',
      '.account__menu-region button:active *,',
      '.account__menu-region button:focus-visible *,',
      '.account__menu-region summary:hover *,',
      '.account__menu-region summary:active *,',
      '.account__menu-region summary:focus-visible *,',
      '.account__menu-region [role="button"]:hover *,',
      '.account__menu-region [role="button"]:active *,',
      '.account__menu-region [role="button"]:focus-visible *{',
      '  color:' + FOREST + '!important;',
      '}',
      /* No media query: applies at every width so the mega menu matches. */
    ].join('');
    const LOGIN_FORM_CSS = [
      '.login-form__email-label,.login-form__email-label *{',
      '  color:' + SAND + '!important;opacity:1!important;',
      '}',
      'input{color:' + SAND + '!important;}',
      /* All widths, not max-750: see note in ACCOUNT_CSS. */
      '.login-form__email-label,input,label{font-size:1.125rem!important;}',
      '.login-form__divider,.login-form__divider *{font-size:1rem!important;}',
      /* Consent line and its tick box: size and alignment. */
      'label:has(input[type="checkbox"]),label:has(input[type="checkbox"]) *,',
      'small,small *,.login-form__marketing,.login-form__marketing *{',
      '  font-size:1.0625rem!important;',
      '}',
      /* Centre the box on its text: the checkbox's own parent is the align target. */
      '*:has(> input[type="checkbox"]){',
      '  display:flex!important;align-items:center!important;gap:.5rem!important;',
      '}',
      /* Mobile only; desktop out of scope. */
      '@media (max-width:750px){',
      /* Email label matches the menu links: one size for both, mobile only. */
      '  .login-form__email-label,.login-form__email-label *,',
      '  .login-form__email-input{font-size:1.25rem!important;}',
      /* OR divider: larger and medium, mobile only. */
    '  .login-form__divider,.login-form__divider *{',
    '    font-size:1.25rem!important;font-weight:500!important;',
    '  }',
      '  label:has(input[type="checkbox"]),label:has(input[type="checkbox"]) *,',
      '  small,small *,.login-form__marketing,.login-form__marketing *{',
      '    font-size:1.1875rem!important;',
      '  }',
      '}',
      'input[type="checkbox"]{',
      '  flex:0 0 auto!important;margin:0!important;',
      '  align-self:center!important;vertical-align:middle!important;',
      '}',
      /* Submit arrow: full Sand in every state, including [disabled] (Shopify dims it). */
      '.login-form__email-submit,',
      '.login-form__email-submit.button,',
      '.login-form__email-submit.button.primary,',
      '.login-form__email-submit[disabled],',
      '.login-form__email-submit[disabled].button,',
      '.login-form__email-submit[disabled].button.primary,',
      '.login-form__email-submit:disabled{',
      '  color:' + SAND + '!important;',
      '  opacity:1!important;',
      '  filter:none!important;',
      '}',
      '.login-form__email-submit svg,',
      '.login-form__email-submit .login-form__email-submit-icon,',
      '.login-form__email-submit-icon{',
      '  color:' + SAND + '!important;',
      '  opacity:1!important;',
      '  fill-opacity:1!important;',
      '  stroke-opacity:1!important;',
      '  filter:none!important;',
      '}',
      '.login-form__email-submit svg path,',
      '.login-form__email-submit-icon path{',
      '  stroke:' + SAND + '!important;',
      '  stroke-opacity:1!important;',
      '  opacity:1!important;',
      '}',
      /* Email label smaller (label only, so typed text keeps its size). Last rule in the
         sheet and outside the media query, so it wins at both breakpoints. */
      '.login-form__email-label,.login-form__email-label *{font-size:0.9375rem!important;}'
    ].join('');
    /* Keyed by root AND by stylesheet: a single WeakSet of roots let the first sheet to
       reach a root block the second. WeakMap of root -> Set of tags lets each root take each
       sheet once. */
    const done = new WeakMap();
    /* Re-adopt, do not adopt-once: the component ASSIGNS root.adoptedStyleSheets on open,
       dropping every sheet that is not its own, so ours must be put back whenever missing.
       Track the sheet, not a boolean; the check is a cheap includes(). */
    const adopt = (root, css, tag) => {
      if (!root) return;
      let made = done.get(root);
      if (!made) { made = new Map(); done.set(root, made); }
      let sheet = made.get(tag);
      if (!sheet) {
        try {
          sheet = new CSSStyleSheet();
          sheet.replaceSync(css);
          made.set(tag, sheet);
        } catch (e) { return; }
      }
      try {
        if (!root.adoptedStyleSheets.includes(sheet)) {
          root.adoptedStyleSheets = [...root.adoptedStyleSheets, sheet];
        }
      } catch (e) {}
    };
    /* Heading text override. Must be JS: the heading is a real element and the dialog is
       labelled by it (aria-labelledby), so a ::before would break the accessible name.
       Only fires on Shopify's signed-out string; skips a signed-in or localised heading. */
    /* Translated heading: general.account.sign_in_heading is a real key in all 32 locale
       files, rendered through `t` into data-kch-heading by the liquid; this asset reads that
       attribute (it cannot call `t`), so Translate & Adapt drives it with no code change. */
    const renameHeading = (root, account) => {
      if (!root) return;
      try {
        const text = account && account.dataset ? (account.dataset.kchHeading || '') : '';
        if (!text || text.indexOf('Translation missing') === 0) return;
        const h = root.querySelector('#dialog-heading');
        if (!h) return;
        /* Only ever replace the sign-in heading, never another state's, never our own text. */
        if (h.textContent === text) return;
        if (!/sign\s*in|connect|anmeld|melden|inicia|sesion|conta|accedi/i.test(h.textContent)) return;
        h.textContent = text;
      } catch (e) {}
    };
    /* Consent label (KCH, 5 Oct 2026). Shopify's Arabic strings for this sheet (the
       customerAccountTranslations payload on the page) carry no marketing_consent_label, so
       the checkbox fell back to "Email me with news and offers" on Arabic pages.
       layout/theme.liquid writes general.account.marketing_consent into
       meta[name="kch-consent-label"] on Arabic pages only; elsewhere there is no meta and
       this does nothing. Only the text node is changed, and only while it still reads
       Shopify's English default, so a future Shopify translation wins on its own. */
    const CONSENT_EN = 'Email me with news and offers';
    const consentText = () => {
      const m = document.querySelector('meta[name="kch-consent-label"]');
      const t = m ? (m.getAttribute('content') || '') : '';
      return (!t || t.indexOf('Translation missing') === 0) ? '' : t;
    };
    const renameConsent = (root) => {
      const text = consentText();
      if (!root || !text) return;
      try {
        root.querySelectorAll('.login-form__marketing-consent-label').forEach((label) => {
          label.childNodes.forEach((node) => {
            if (node.nodeType === 3 && node.nodeValue.trim() === CONSENT_EN) node.nodeValue = text;
          });
        });
      } catch (e) {}
    };
    /* The form renders inside its own shadow root, which the account observer cannot see
       into, so each form root gets one observer. Our own write leaves no English to match,
       so the observer settles after one pass. */
    const consentWatched = new WeakSet();
    const watchConsent = (root) => {
      if (!root || consentWatched.has(root) || !consentText()) return;
      consentWatched.add(root);
      renameConsent(root);
      try {
        new MutationObserver(() => renameConsent(root)).observe(root, { childList: true, subtree: true, characterData: true });
      } catch (e) {}
    };
    const reachLoginForm = (account) => {
      /* The login form loads lazily and may be slotted (light DOM) or in the account root;
         look in both, and adopt on both roots (slotted markup is styled from the account root). */
      adopt(account.shadowRoot, LOGIN_FORM_CSS, 'loginform');
      const scopes = [account, account.shadowRoot].filter(Boolean);
      for (const scope of scopes) {
        scope.querySelectorAll('shopify-login-form, shopify-login-form-loader').forEach((el) => {
          adopt(el.shadowRoot, LOGIN_FORM_CSS, 'loginform');
          watchConsent(el.shadowRoot);
          el.querySelectorAll('shopify-login-form').forEach((inner) => {
            adopt(inner.shadowRoot, LOGIN_FORM_CSS, 'loginform');
            watchConsent(inner.shadowRoot);
          });
        });
      }
    };
    /* Three <shopify-account> elements exist in this theme (dock, drawer, mega menu), not two. */
    const wired = new WeakSet();
    /* Close on outside click: corrected to the real dialog/backdrop geometry. */
    const wireDismiss = (account, root) => {
      const dialog = root.querySelector('dialog');
      if (!dialog || dialog.dataset.kchDismiss) return;
      dialog.dataset.kchDismiss = '1';
      dialog.addEventListener('click', (e) => {
        if (e.target !== dialog) return;
        if (typeof account.close === 'function') account.close();
      });
    };
    /* One global modality flag, shared by every account element on the page. Set by a Tab
       keydown, cleared by any pointerdown, both in the capture phase. Installed once. */
    const KB_KEY = '__kchKbNav';
    if (!window[KB_KEY + 'Wired']) {
      window[KB_KEY + 'Wired'] = 1;
      window[KB_KEY] = false;
      document.addEventListener('keydown', (e) => {
        if (e.key === 'Tab') window[KB_KEY] = true;
      }, true);
      document.addEventListener('pointerdown', () => { window[KB_KEY] = false; }, true);
    }
    /* Press feedback, driven from pointer events rather than :active. See the .kch-pressed
       note in ACCOUNT_CSS for why. Guarded by a data flag so the sweep can re-run it freely,
       matching wireDismiss above. Listeners are attached to the button itself, so they go
       with it if the component re-renders and a fresh button gets a fresh flag. */
    const wirePress = (root) => {
      const btn = root.querySelector('.account__close');
      if (!btn || btn.dataset.kchPress) return;
      btn.dataset.kchPress = '1';
      const on  = () => btn.classList.add('kch-pressed');
      const off = () => btn.classList.remove('kch-pressed');
      btn.addEventListener('pointerdown', on);
      ['pointerup', 'pointercancel', 'pointerleave', 'blur'].forEach((e) => {
        btn.addEventListener(e, off);
      });
      /* Keyboard modality. Capture phase on the document so it is seen before anything can
         stop propagation, and armed only by Tab: a programmatic focus() on dialog open never
         arms it, which is the whole point. */
      btn.addEventListener('focus', () => {
        if (window[KB_KEY]) btn.classList.add('kch-kbfocus');
      });
      btn.addEventListener('blur', () => btn.classList.remove('kch-kbfocus'));
    };
    const wire = (account) => {
      if (wired.has(account)) return;
      if (!account.shadowRoot) return false;   /* not upgraded yet: caller retries */
      wired.add(account);
    /* wireDrag is a const on the module, so it must be defined before first use. */
      try {
    /* Both sheets on first wire, so nothing waits for the observer. */
        adopt(account.shadowRoot, ACCOUNT_CSS, 'account');
        reachLoginForm(account);
        renameHeading(account.shadowRoot, account);
    /* Dialog chunk and login form load lazily; re-adopt when they arrive. */
        account.addEventListener('open', () => {
        adopt(account.shadowRoot, ACCOUNT_CSS, 'account');
        reachLoginForm(account);
        setTimeout(() => reachLoginForm(account), 400);
        setTimeout(() => reachLoginForm(account), 1500);
        });
        const obs = new MutationObserver(() => {
        adopt(account.shadowRoot, ACCOUNT_CSS, 'account');
        reachLoginForm(account);
        renameHeading(account.shadowRoot, account);
        });
        obs.observe(account.shadowRoot, { childList: true, subtree: true });
    /* No disconnect: the observer must outlive the first 20s; the sheet can open much later. */
      } catch (e) {}
      return true;
    };
    /* A sweep, not a snapshot: re-scan on each observer hit so late-injected roots are caught. */
    const CANARY = [
      ['dialog.dialog, .dialog',     'the sheet itself: all sizing, backdrop and radius'],
      ['#dialog-heading',            'the heading, and the translated rename'],
      ['.account__close',            'the close button ring and Sand fill'],
      ['.account__menu-region',      'menu link sizing, weight and the Sand hover'],
      ['.account__content',          'sheet padding']
    ];
    const canaryChecked = new WeakSet();
    const canary = (account, root) => {
      try {
        if (canaryChecked.has(account)) return;
    /* Match by tag, not class: class-based matching missed re-injected elements. */
        const dialog = root.querySelector('dialog');
        /* Only meaningful once the component has rendered its content. */
        if (!dialog || !dialog.open) return;
        canaryChecked.add(account);
        const missing = CANARY.filter((row) => !root.querySelector(row[0]));
        if (!missing.length) return;
        /* eslint-disable-next-line no-console */
        console.warn(
          '[KCH] The Shopify account component no longer matches selectors this theme styles.\n' +
          'Shopify updates this component independently of the theme, so this is expected eventually.\n' +
          'The sheet still works and still takes its colours from the documented CSS variables;\n' +
          'the KCH styling below has silently stopped applying:\n' +
          missing.map((row) => '  ' + row[0] + '  ->  ' + row[1]).join('\n') +
          '\nFix: re-point these in assets/kch-custom.js (ACCOUNT_CSS / LOGIN_FORM_CSS).'
        );
      } catch (e) {}
    };
    /* KCH a11y 22 Oct 2026 (R15, WCAG 2.5.3 Label in Name): Shopify's signed-out avatar button inside
       the shadow root carries aria-label="Account", while the slotted content shows "Log in" (or
       "Connexion", "Anmelden", ...; "Account" text when signed in comes from our own locale string).
       axe flagged label-content-name-mismatch on the drawer copy, live and preview alike. The button's
       name is set to the slot's own text, so the spoken name always starts with what is on screen,
       and kept there if Shopify re-renders the attribute. */
    const nameButton = (root) => {
      const btn = root.querySelector('[part="signed-out-avatar"]');
      if (!btn) return;
      const slot = btn.querySelector('slot') || root.querySelector('slot[name="signed-out-avatar"]');
      const nodes = slot && slot.assignedElements ? slot.assignedElements({ flatten: true }) : [];
      /* The drawer copy is a hover-button whose .btn-fill holds a lockstep duplicate of the label;
         read each slotted element without that copy, or the name doubles ("Login Login"). */
      const textOf = (n) => { const c = n.cloneNode(true); c.querySelectorAll('[data-fill], .btn-fill').forEach((f) => f.remove()); return (c.textContent || '').replace(/\s+/g, ' ').trim(); };
      const label = nodes.map(textOf).join(' ').trim();
      if (!label) return;
      /* Remove the fixed aria-label rather than rewrite it: the slotted text (whose .btn-fill copy is
         aria-hidden) then names the button, so the name is exactly the visible label in every language.
         Rewriting it failed axe because axe counts the lockstep copy as visible text. */
      const apply = () => { if (btn.hasAttribute('aria-label')) btn.removeAttribute('aria-label'); };
      apply();
      if (!btn.__kchNameObs) {
        try {
          btn.__kchNameObs = new MutationObserver(apply);
          btn.__kchNameObs.observe(btn, { attributes: true, attributeFilter: ['aria-label'] });
        } catch (e) {}
      }
    };
    const sweep = () => {
      for (const el of document.querySelectorAll('shopify-account')) {
        try {
          wire(el);
      /* wire() returns early once wired, so the re-adopt has to run before that guard. */
          if (el.shadowRoot) {
            adopt(el.shadowRoot, ACCOUNT_CSS, 'account');
            reachLoginForm(el);
            renameHeading(el.shadowRoot, el);
      /* Guarded by a data flag on the dialog, so re-running is free. */
            wireDismiss(el, el.shadowRoot);
            wirePress(el.shadowRoot);
            nameButton(el.shadowRoot);
            canary(el, el.shadowRoot);
          }
        } catch (e) {}
      }
    };
    /* Leak fix: bound the observer/poll so they cannot run forever on a page that never opens the sheet. */
    const KEY = '__kchAccountBoot';
    const teardown = () => {
      const reg = window[KEY];
      if (!reg) return;
      try { reg.obs && reg.obs.disconnect(); } catch (e) {}
      try { reg.timer && clearInterval(reg.timer); } catch (e) {}
      try { document.removeEventListener('pointerdown', sweep, true); } catch (e) {}
      window[KEY] = null;
    };
    const start = () => {
      /* Idempotent by design: a second call re-sweeps and allocates nothing. */
      if (window[KEY]) { sweep(); return; }
      const reg = { obs: null, timer: null };
      window[KEY] = reg;
      sweep();
      let tries = 0;
      reg.timer = setInterval(() => {
        tries += 1;
        sweep();
        if (tries > 40) { clearInterval(reg.timer); reg.timer = null; }
      }, 250);
      try {
        let queued = false;
        reg.obs = new MutationObserver(() => {
          if (queued) return;
          queued = true;
          requestAnimationFrame(() => { queued = false; sweep(); });
        });
        reg.obs.observe(document.documentElement, { childList: true, subtree: true });
      } catch (e) {}
    /* The case no poll covers: the mega menu opened long after load. */
      document.addEventListener('pointerdown', sweep, true);
    };
    if (window.customElements && customElements.whenDefined) {
      customElements.whenDefined('shopify-account').then(start).catch(() => {});
    }
    /* Theme editor re-renders sections; re-wire the dock and drawer on section load. */
    document.addEventListener('shopify:section:load', start);
    document.addEventListener('shopify:section:unload', teardown);
    /* The drawer's markup exists at parse time, so catch it on DOM ready too. */
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', start);
    } else {
      start();
    }
  })();
  })();
})();
/* ==========================================================================
   Quick view: honour the variant chosen on the card          added 24 Jul 2026
   --------------------------------------------------------------------------
   Concept's QuickView builds its fetch URL as
     this.getAttribute('data-product-url').split('?')[0] + '?view=modal'
   (theme.js, QuickView.quickview). The split throws the query away, so the
   ?variant=ID this file writes onto data-product-url when a swatch is picked
   never reaches the modal and it always renders selected_or_first_available.
   Fixing that in theme.js is the root fix but a theme update overwrites it, so
   this lives here instead. STOPGAP by choice, Sean's call 24 Jul.
   It rides the theme's own quickview:loaded event, whose detail.productUrl is
   the unsplit attribute and therefore still carries the variant. Nothing here
   overrides or copies a theme method, so a theme update cannot silently drop a
   patched line; if the event ever goes away this stops working visibly rather
   than half working.
   ========================================================================== */
(function () {
  if (window.__kchQuickViewVariant) return;
  window.__kchQuickViewVariant = true;
  function variantIdFrom(url) {
    if (!url) return null;
    var q = String(url).split('?')[1];
    if (!q) return null;
    var id = new URLSearchParams(q).get('variant');
    return id ? String(id) : null;
  }
  /* Set every option input the variant needs, then fire one change so the picker
     recalculates once rather than once per option and never lands on a partial
     combination mid-way. */
  function applyVariant(scope, id) {
    var picker = scope.querySelector('variant-picker');
    if (!picker) return false;
    var holder = picker.querySelector('[data-variants]');
    if (!holder) return false;
    var variants;
    try { variants = JSON.parse(holder.textContent); } catch (e) { return false; }
    if (!Array.isArray(variants)) return false;
    var target = null;
    for (var i = 0; i < variants.length; i++) {
      if (String(variants[i].id) === id) { target = variants[i]; break; }
    }
    if (!target) return false;
    var values = [target.option1, target.option2, target.option3];
    var last = null;
    for (var v = 0; v < values.length; v++) {
      if (values[v] == null) continue;
      var inputs = picker.querySelectorAll('input[data-option-value]');
      for (var k = 0; k < inputs.length; k++) {
        var el = inputs[k];
        if (el.getAttribute('data-option-value') !== values[v]) continue;
        if (!el.checked) { el.checked = true; last = el; }
        else if (!last) { last = null; }
        break;
      }
    }
    if (last) last.dispatchEvent(new Event('change', { bubbles: true }));
    return true;
  }
  document.addEventListener('quickview:loaded', function (e) {
    var id = variantIdFrom(e.detail && e.detail.productUrl);
    if (!id) return;
    var modal = e.target && e.target.querySelector ? e.target : document;
    /* the theme injects the markup and then upgrades the custom elements, so give
       the picker a frame to define itself before reaching into it */
    requestAnimationFrame(function () {
      if (!applyVariant(modal, id)) {
        requestAnimationFrame(function () { applyVariant(modal, id); });
      }
    });
  });
})();
/* ============================================================================
   Quick view from the photo and the title                       25 Jul 2026
   --------------------------------------------------------------------------
   Sean's call: in the KCH carousel and reviews sections, clicking the product
   photo or the product title opens quick view rather than navigating to the
   product page. Stock Concept only opens it from the small eye button, which
   on a phone is a 40px target in a corner while the photo fills the card.
   Delegated from the document so it covers sections rendered after load and
   anything the section re-renders in the editor. Scoped to our two sections,
   so every other product card in the theme keeps its normal link behaviour.
   The click is forwarded to the card's own quick view button rather than
   reimplementing the modal: that button carries aria-controls pointing at its
   own <quick-view> element, and the theme owns opening it. If a card has no
   quick view button, because the setting is off or the product has no media,
   nothing is intercepted and the link works as normal.
   Modified clicks are left alone so opening in a new tab still reaches the
   product page, which is otherwise unreachable from the card. */
(function () {
  var SECTIONS = '[data-kch-carousel], [data-kch-reviews]';
  function quickViewButtonFor(el) {
    var card = el.closest('.product-card, .kch-review-card');
    if (!card) return null;
    return card.querySelector('.quick-view__button');
  }
  document.addEventListener('click', function (e) {
    /* let the browser have modified clicks: new tab, new window, download */
    if (e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
    var target = e.target.closest(
      '.product-card__media a, a.product-card__title, .kch-review-card__media'
    );
    if (!target) return;
    if (!target.closest(SECTIONS)) return;
    /* the quick add button and the eye itself sit inside the media box; leave them */
    if (e.target.closest('.quick-add, .quick-view__button')) return;
    var btn = quickViewButtonFor(target);
    if (!btn) return;
    e.preventDefault();
    btn.click();
  }, true);
  /* the review card photo is not a link, so give it the affordance of one */
  function markPointers(root) {
    var scope = root && root.querySelectorAll ? root : document;
    Array.prototype.forEach.call(scope.querySelectorAll(SECTIONS), function (sec) {
      Array.prototype.forEach.call(sec.querySelectorAll('.kch-review-card__media'), function (m) {
        if (m.querySelector('.quick-view__button') || (m.closest('.kch-review-card') || {}).querySelector) {
          m.style.cursor = 'pointer';
        }
      });
    });
  }
  if (document.readyState !== 'loading') markPointers(document);
  else document.addEventListener('DOMContentLoaded', function () { markPointers(document); });
  document.addEventListener('shopify:section:load', function (e) { markPointers(e.target); });
})();
/* ============================================================================
   View all keeps its distance whatever the cards end with      25 Jul 2026
   --------------------------------------------------------------------------
   Sean's brief: the gap between the bottom of the cards and the View all button
   should adapt, because a card with variants ends in a swatch row and one
   without ends at its price, and those leave different amounts of trailing
   space inside the card. A fixed margin is right for one case and wrong for
   the other.
   Compensating in CSS by differencing the theme's clamps did not work: the
   computed trailing space was 30.6px where the clamps predicted 18.2, because
   several rules contribute and only the browser knows the resolved total. So
   the trailing space is measured from the painted boxes: the distance from the
   last thing a shopper can actually see, the swatch row or the price, down to
   the bottom of the card. The margin is then whatever makes the total reach the
   target, floored at zero.
   Runs per section, so two carousels on one page can differ. Re-measures on
   resize and on section load, since the clamps are viewport dependent. */
(function () {
  var TARGET_BASE = 32;   /* px from the last visible element to the button */
  function lastVisible(card) {
    return card.querySelector('.product-card__spec') ||
           card.querySelector('.price') ||
           card.querySelector('.product-card__content');
  }
  function tune(section) {
    var below = section.querySelector('.kch-carousel__viewall');
    if (!below) return;
    var card = section.querySelector('.product-card, .kch-collection-card');
    if (!card) return;
    var el = lastVisible(card);
    if (!el) return;
    var cardBottom = card.getBoundingClientRect().bottom;
    var lastBottom = el.getBoundingClientRect().bottom;
    var trailing = Math.max(0, cardBottom - lastBottom);
    /* the target scales with the viewport the way the theme's own spacing does */
    var target = TARGET_BASE * (window.innerWidth >= 1400 ? 1.25 : 1);
    section.style.setProperty('--kch-viewall-gap', Math.max(0, Math.round(target - trailing)) + 'px');
  }
  function tuneAll(root) {
    var scope = root && root.querySelectorAll ? root : document;
    Array.prototype.forEach.call(
      scope.querySelectorAll('[data-kch-carousel]'), tune);
  }
  function ready(fn) {
    if (document.readyState !== 'loading') fn();
    else document.addEventListener('DOMContentLoaded', fn);
  }
  ready(function () { tuneAll(document); });
  window.addEventListener('resize', function () { tuneAll(document); });
  document.addEventListener('shopify:section:load', function (e) { tuneAll(e.target); });
  /* ---------------------------------------------------------------------------
     featured-collections View all aligner                          25 Jul 2026
     -------------------------------------------------------------------------
     A static CSS offset for the beside-the-arrows View all cannot be right for
     every case: the columns setting resizes the cards, and in slider mode the grid's
     right edge does not always land on the page-width edge. So measure it. This reads
     the real right edge of the tab's card grid (no arrows: hug it) or the arrow row
     (arrows: sit just left of them) and sets the button's inset accordingly. Sean 25
     Jul: the answer is in what Concept renders, not what the source implies. */
  function kchAlignViewAll(section) {
    var strips = section.querySelectorAll ? section.querySelectorAll('.tab-list') : [];
    Array.prototype.forEach.call(strips, function (strip) {
      var buttons = strip.querySelectorAll('.kch-viewall-tab');
      if (!buttons.length) return;
      var stripRect = strip.getBoundingClientRect();
      var gap = parseFloat(getComputedStyle(strip).columnGap) || 10;
      var panels = section.querySelectorAll('[id^="TabPanelContent-"]');
      Array.prototype.forEach.call(buttons, function (btn) {
        var idx = btn.getAttribute('data-index');
        var arrows = strip.querySelector('.indicators[data-index="' + idx + '"]');
        var hasArrows = arrows && arrows.getBoundingClientRect().width > 0 &&
          getComputedStyle(arrows).display !== 'none' && arrows.children.length > 0;
        if (hasArrows) {
          var aRect = arrows.getBoundingClientRect();
          btn.style.insetInlineEnd = Math.round(stripRect.right - aRect.left + gap) + 'px';
        } else {
          var panel = panels[idx];
          var grid = panel && panel.querySelector('.card-grid, .product-grid, motion-list');
          if (grid) {
            var gRect = grid.getBoundingClientRect();
            btn.style.insetInlineEnd = Math.round(Math.max(0, stripRect.right - gRect.right)) + 'px';
          } else {
            btn.style.insetInlineEnd = '0px';
          }
        }
      });
    });
  }
  function kchAlignAll(root) {
    var scope = root && root.querySelectorAll ? root : document;
    var sections = scope.querySelectorAll('.section, [id^="shopify-section"]');
    Array.prototype.forEach.call(sections, kchAlignViewAll);
    if (scope.querySelectorAll('.tab-list').length && scope !== document) kchAlignViewAll(scope);
  }
  ready(function () { kchAlignAll(document); });
  window.addEventListener('resize', function () { kchAlignAll(document); });
  document.addEventListener('shopify:section:load', function (e) { kchAlignAll(e.target); });
  document.addEventListener('click', function (e) {
    if (e.target && e.target.closest && e.target.closest('.tab__item')) {
      setTimeout(function () { kchAlignAll(document); }, 60);
    }
  });
})();
/* ============================================================================
   Measurements chart, warmed after LCP                          23 Aug 2026
   --------------------------------------------------------------------------
   THE FAULT. snippets/kch-measurements.liquid line 53 injects a loading
   attribute into the rich-text image inside the Measurements modal:
     {{ page.content | replace: '<img ', '<img loading="lazy" ' }}
   That was added 8 Aug 2026 to stop the chart downloading on every product page
   load for a drawer nobody had opened. It does stop that, and it costs far more
   than it saves.
   Per the HTML Living Standard the lazy load algorithm defers the fetch until the
   element intersects the viewport, and "intersect the viewport" requires the
   element to be BEING RENDERED, meaning it has a CSS layout box. The modal carries
   the hidden attribute, whose UA style is display:none, so nothing inside it has a
   layout box and it can never intersect. The fetch therefore cannot begin until
   the modal is already open, which is the worst possible moment.
   MEASURED ON THE LIVE PRODUCT PAGE, 23 Aug 2026:
     click        modal opens at 84px, the title bar alone
     +32ms        the chart request finally starts
     click to the chart actually rendering:
       554ms      unthrottled
       1,667ms    1.6 Mbps down, 150ms latency
   Seven surfaces render this snippet, so every one of them is affected:
   main-product, featured-product, main-product-modal, and the four
   product-variant-picker snippets. It is not a quick-view fault.
   WHY NOT THE OBVIOUS FIXES.
   Reverting to eager puts 197 KB back on every product page load. Adding
   fetchpriority="low" to an eager image is close to a no-op, because Chromium
   already assigns Low priority to images that are not in the viewport at layout
   time, and priority is queue position rather than bandwidth: the bytes still
   compete during a load that runs 26 seconds on mid-tier mobile.
   Warming on hover alone does not reach either. Published intent lead times are
   200 to 300ms on desktop hover and about 90ms on touch, against a fetch that
   takes 554 to 1,667ms.
   WHAT THIS DOES INSTEAD. Everything Lighthouse scores on this page is settled
   early: measured FCP 2,060ms and LCP 2,628ms on emulated mobile, and CLS and
   Speed Index are untouched because the chart is hidden and paints nothing. So
   the image is warmed into the HTTP cache after that point, at low priority, on
   the first of two triggers:
     idle     requestIdleCallback with a 4s ceiling, registered at DOMContentLoaded.
              The ceiling carries this, not the idle: on a page still fetching 247
              resources a genuinely idle main thread may not arrive for some time.
     intent   pointerover, pointerdown or focusin on the trigger, for anyone who
              reaches the picker before idle fires.
   The markup keeps loading="lazy", so a visitor who leaves early downloads nothing
   and Lighthouse's offscreen-images audit stays clean. When the modal does open,
   the lazy image resolves from cache: the CDN sends
   cache-control: public, max-age=31557600 with vary: Accept, and this warm request
   carries the same Accept header the img would, so it is the same cache entry.
   Measured with this block in place: click to chart rendering falls from 554ms to
   25ms unthrottled and from 1,667ms to 28ms throttled, both reported by the
   browser as already complete before the click landed.
   pointerover and focusin rather than pointerenter and focus, because only the
   former of each pair bubbles and this is delegated. Delegation on document is
   mandatory rather than tidy: main-product-modal's markup arrives over the Section
   Rendering API and does not exist at first paint, which is also why
   quickview:loaded and shopify:section:load are hooked.
   A detached Image is used rather than <link rel=preload>, which logs a console
   warning when a preload goes unused for a few seconds. decode() is called so the
   decode is off the open path too; it is supported on detached images in Chromium
   and rejects harmlessly elsewhere, hence the catch.
   NOT TOUCHED: the sizing system in kch-custom.css. [id^=Measurements-]
   .drawer__inner derives its WIDTH from calc(94vh - var(--kch-mm-chrome)) and the
   image runs width:auto/height:auto with a max-height off the same variable, so
   the layout depends on the image's intrinsic dimensions being known. A fixed
   aspect-ratio would suit the square cave-bed chart and break any chart with other
   proportions. Nothing in this block reads or writes a layout property.
   ========================================================================== */
(() => {
  'use strict';
  if (window.kchMeasureWarm) return;
  window.kchMeasureWarm = true;
  const MODAL = 'x-modal[id^="Measurements-"], .x-modal[id^="Measurements-"]';
  const TRIGGER = '[aria-controls^="Measurements-"]';
  const IDLE_CEILING = 4000;
  /* One request per URL for the life of the page. Several surfaces can render the
     same chart at once (a product page and a featured-product section showing the
     same product), and the quick view rebuilds its markup on every open. */
  const warmed = new Set();
  const warmUrl = (url) => {
    if (!url || warmed.has(url)) return;
    warmed.add(url);
    const probe = new Image();
    /* Explicit rather than implied: a detached image already lands at Low priority
       in Chromium, but saying so survives that heuristic changing. */
    if ('fetchPriority' in probe) probe.fetchPriority = 'low';
    probe.decoding = 'async';
    probe.src = url;
    if (typeof probe.decode === 'function') probe.decode().catch(() => {});
  };
  const warmScope = (root) => {
    const scope = (root && root.querySelectorAll) ? root : document;
    scope.querySelectorAll(MODAL).forEach((modal) => {
      modal.querySelectorAll('img[src]').forEach((img) => {
        warmUrl(img.getAttribute('src'));
      });
    });
  };
  const onIntent = (event) => {
    const trigger = event.target && event.target.closest
      ? event.target.closest(TRIGGER)
      : null;
    if (!trigger) return;
    const id = trigger.getAttribute('aria-controls');
    const modal = id ? document.getElementById(id) : null;
    if (modal) warmScope(modal);
  };
  ['pointerover', 'pointerdown', 'focusin'].forEach((name) => {
    document.addEventListener(name, onIntent, { capture: true, passive: true });
  });
  const scheduleIdle = () => {
    /* Product pages only, added 23 Aug 2026 after measuring the homepage. The idle
       sweep was pulling the featured product's size chart down on the homepage and
       on both collection pages: 125 KB for a modal that surface's visitors almost
       never open, 12% of homepage image weight. On a product page the chart is the
       thing the shopper is deciding with, so the idle warm earns its bytes there
       and nowhere else. Every other surface keeps the intent triggers below, which
       is what actually covers the click, so nothing regresses; only the speculative
       download goes away. template-product is emitted by the theme on every locale,
       verified on /fr and /ja, and is absent on index, collection and page. */
    if (!document.body.classList.contains('template-product')) return;
    const run = () => warmScope(document);
    if (typeof window.requestIdleCallback === 'function') {
      window.requestIdleCallback(run, { timeout: IDLE_CEILING });
    } else {
      setTimeout(run, IDLE_CEILING);
    }
  };
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', scheduleIdle, { once: true });
  } else {
    scheduleIdle();
  }
  /* The quick view fetches its markup over the Section Rendering API, so its chart
     does not exist at first paint and the idle sweep above cannot have seen it. */
  document.addEventListener('quickview:loaded', (event) => {
    warmScope(event.target && event.target.querySelectorAll ? event.target : document);
  });
  /* Theme editor re-renders sections in place. */
  document.addEventListener('shopify:section:load', (event) => warmScope(event.target));
})();

/* ==========================================================================
   kch-button-lockstep, JS half. The CSS block of the same name in
   kch-custom.css turns every Concept .btn-fill into a clipped, full-size copy of
   the button in its hover colours. This fills that layer.           2 Oct 2026
   For each .button > .btn-fill:
     1. copies the button's own children (the .btn-text, and the .btn-duplicate
        on the filters toggle) into the fill. The copy is paint only: the fill is
        aria-hidden, the copy carries no ids, names, label associations or tab
        stops, and the sort-by listbox is left out because that form must exist
        once;
     2. (until 23 Oct) parked Motion One's variable at -76% inline, so a button
        nobody has hovered stays clipped shut even after Motion registers the
        property with its 0px initial value on the first hover anywhere on the
        page. The park is now in kch-custom.css; see Performance below.
   The copy is rebuilt whenever the label changes (sold-out text on a variant
   change, "Copied" on the gift card button), which a MutationObserver on that
   button sees. Attribute changes are not watched: the theme sets its state
   classes on the button, and the copy follows them through the same stylesheet
   rules as the original.
   theme.js HoverButton is untouched. It still animates --motion-translateY on
   the same element it always did; the stylesheet is what changed.
   Performance, 23 Oct 2026. Until then every fill was copied at DOMContentLoaded
   (78 buttons, 324 elements on the home page, none of them in the first phone
   screen) and one observer watched every change on the whole page. Now:
     - the -76% park is a stylesheet value on .button > .btn-fill (kch-custom.css),
       so nothing is written at load. Motion's inline value overrides it once a
       button has been hovered, as the inline park did;
     - a fill is copied when a pointer first moves over its button (mouse, pen or
       touch: pointerover comes before pointerenter, pointerdown and :active) or
       when the button takes focus (keyboard :active comes after focus). Those are
       the only ways a fill opens, so no fill is seen before it is copied;
     - each copied button gets its own observer; buttons that arrive later (cart
       drawer re-render, quick view) are copied on first use like the rest.
   ========================================================================== */
(() => {
  'use strict';
  if (window.kchButtonLockstep) return;
  window.kchButtonLockstep = true;
  const NOT_COPIED = ['btn-fill', 'btn-loader'];

  const strip = (root) => {
    const nodes = [root].concat(Array.from(root.querySelectorAll('*')));
    for (let i = 0; i < nodes.length; i++) {
      const el = nodes[i];
      el.removeAttribute('id');
      el.removeAttribute('name');
      el.removeAttribute('for');
      el.removeAttribute('tabindex');
      el.removeAttribute('aria-controls');
      el.removeAttribute('aria-describedby');
      el.removeAttribute('aria-labelledby');
      if (el.matches('input, button, select, textarea, a[href], [contenteditable]')) {
        el.setAttribute('tabindex', '-1');
        if ('disabled' in el) el.disabled = true;
      }
    }
    if ('inert' in root) root.inert = true;
  };

  const build = (btn) => {
    const fill = btn.querySelector(':scope > .btn-fill');
    if (!fill) return;
    const copies = [];
    const kids = Array.from(btn.children);
    for (let i = 0; i < kids.length; i++) {
      const child = kids[i];
      if (child === fill) continue;
      if (NOT_COPIED.some((c) => child.classList.contains(c))) continue;
      if (child.tagName === 'SCRIPT' || child.tagName === 'TEMPLATE' || child.tagName === 'STYLE') continue;
      const copy = child.cloneNode(true);
      copy.querySelectorAll('.sort-listbox, script, template, style').forEach((n) => n.remove());
      strip(copy);
      copies.push(copy);
    }
    fill.replaceChildren.apply(fill, copies);
    fill.setAttribute('aria-hidden', 'true');
  };

  /* Mutations inside the fill are this module's own writes, or Motion's; both are
     ignored. Anything else that changes under a copied button rebuilds its fill. */
  const onMutations = (records) => {
    const dirty = new Set();
    for (let r = 0; r < records.length; r++) {
      const rec = records[r];
      const target = rec.target.nodeType === 1 ? rec.target : rec.target.parentElement;
      if (!target || target.closest('.btn-fill')) continue;
      const btn = target.closest('.button');
      if (btn && btn._kchLockstep) dirty.add(btn);
    }
    dirty.forEach((btn) => { if (btn.isConnected) build(btn); });
  };
  const watcher = new MutationObserver(onMutations);

  const ensure = (event) => {
    const t = event.target;
    if (!t || t.nodeType !== 1 || !t.closest) return;
    const btn = t.closest('.button');
    if (!btn || btn._kchLockstep || !btn.querySelector(':scope > .btn-fill')) return;
    btn._kchLockstep = true;
    build(btn);
    watcher.observe(btn, { childList: true, subtree: true, characterData: true });
  };
  document.addEventListener('pointerover', ensure, { capture: true, passive: true });
  document.addEventListener('focusin', ensure, { capture: true });
})();
/* Input modality, 21 Oct 2026 (PERF-003, WCAG 2.4.7).
   Sets html[data-kch-modality] to "keyboard" when a key that moves focus or selection is
   pressed and to "pointer" on any mouse, pen or touch press. kch-custom.css uses it to
   cancel the swatch focus outline only for pointer use, so a tap never draws the outline
   (the 30 Jul phone report) and a keyboard user always sees where focus is. Listeners
   are passive and capture phase, so nothing else on the page is affected.
   31 Oct 2026: kch-custom.css also uses "pointer" to hide every focus ring site-wide after
   a mouse or touch press (Sean: a ring must only ever show for Tab use). Typing in a text
   field is not navigation, so inside a text field only Tab switches to "keyboard";
   otherwise a space typed after clicking into the search box would bring the ring back. */
(() => {
  'use strict';
  if (window.kchModality) return;
  window.kchModality = true;
  const root = document.documentElement;
  const NAV_KEYS = ['Tab', 'ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight', 'Home', 'End', ' ', 'Enter'];
  const TEXT_TYPES = /^(text|search|email|tel|url|password|number|date|datetime-local|month|time|week)$/;
  const isTextEntry = (el) => !!el && (el.isContentEditable || el.tagName === 'TEXTAREA' ||
    (el.tagName === 'INPUT' && TEXT_TYPES.test(el.type || 'text')));
  const set = (mode) => { if (root.getAttribute('data-kch-modality') !== mode) root.setAttribute('data-kch-modality', mode); };
  document.addEventListener('keydown', (e) => {
    if (e.metaKey || e.ctrlKey || e.altKey) return;
    if (e.key !== 'Tab' && isTextEntry(e.target)) return;
    if (NAV_KEYS.includes(e.key)) set('keyboard');
  }, { capture: true, passive: true });
  document.addEventListener('pointerdown', () => set('pointer'), { capture: true, passive: true });
})();
/* Section framing for keyboard focus, 22 Oct 2026 (a11y R6-4).
   Chrome scrolls a newly focused control into view only when it is off screen, and then
   centres the control, not its section, so Tab from the last product into the reviews left
   the page straddling the two sections. When Tab or Shift+Tab moves focus into a different
   section of <main> on the home page or a collection page, the page scrolls once so that
   section sits in the band between the sticky header (html scroll-padding-top) and the
   bottom of the viewport: centred if it fits; otherwise its top under the header (Tab) or
   its bottom at the viewport bottom (Shift+Tab), moved only as far as needed to keep the
   focused control fully visible with a 24 px margin (a control taller than the band shows
   its top).
   Inside a section it acts only when the focused control, or its whole card when the card
   fits, is cut off or within 24 px of the band edge, and moves just enough to clear it.
   It runs in focusin, before the browser's own scroll into view, so the page moves once
   per key press. Section entry is instant, as the browser's own focus scroll is (a smooth
   scroll there could be cut short by the browser, seen in Arabic); the small in-section
   nudge is smooth unless prefers-reduced-motion is set.
   Not handled here: header, drawers, quick view, footer (R5-2 FooterParallax), back to
   top (overlay group) and #MainContent itself (R5-3). Mouse and touch focus never apply:
   the last key must be Tab, pressed within the last half second. */
(() => {
  'use strict';
  if (window.kchSectionFrame) return;
  window.kchSectionFrame = true;
  const body = document.body;
  if (!body || !/\btemplate-(index|collection)\b/.test(body.className)) return;
  const reduce = window.matchMedia('(prefers-reduced-motion: reduce)');
  const EDGE = 24;
  let back = false;
  let tabAt = -1e9;
  let lastSection = null;
  document.addEventListener('keydown', (e) => {
    if (e.key !== 'Tab') return;
    back = e.shiftKey;
    tabAt = performance.now();
  }, { capture: true, passive: true });
  document.addEventListener('pointerdown', () => { tabAt = -1e9; }, { capture: true, passive: true });

  const sectionOf = (el) => {
    const main = document.querySelector('main');
    if (!main || !el || !el.closest || el === main) return null;
    if (el.closest('quick-view, dialog, [role="dialog"], [aria-modal="true"], .drawer, .modal')) return null;
    const s = el.closest('.shopify-section');
    return s && main.contains(s) ? s : null;
  };
  const band = () => {
    const cs = getComputedStyle(document.documentElement);
    const top = parseFloat(cs.scrollPaddingTop) || 0;
    const bottom = window.innerHeight - (parseFloat(cs.scrollPaddingBottom) || 0);
    return [top, bottom];
  };

  document.addEventListener('focusin', (e) => {
    const el = e.target;
    const section = sectionOf(el);
    const previous = lastSection;
    lastSection = section;
    if (!section || performance.now() - tabAt > 500) return;
    const r = el.getBoundingClientRect();
    if (!r.width && !r.height) return;
    const [top, bottom] = band();
    const H = bottom - top;
    if (H < 120) return;
    let d = 0;
    const entry = section !== previous;
    if (entry) {
      const s = section.getBoundingClientRect();
      if (s.height <= H) d = s.top - top - (H - s.height) / 2;
      else d = back ? s.bottom - bottom : s.top - top;
      /* keep the focused control fully in the band with a 24 px margin, moving the
         section's framing as little as needed; a control taller than the band shows its top */
      if (r.height > H - 2 * EDGE) d = r.top - (top + EDGE);
      else d = Math.min(Math.max(d, r.bottom - (bottom - EDGE)), r.top - (top + EDGE));
    } else {
      let unit = el.closest('.card, .product-card, .kch-review-card') || el;
      if (unit !== el && unit.getBoundingClientRect().height > H - 2 * EDGE) unit = el;
      const u = unit.getBoundingClientRect();
      /* R8-1: a control taller than the band (a review card on a phone) keeps its top in view:
         aligning its bottom pushed the top under the sticky header at every review */
      if (u.height > H - 2 * EDGE) { if (u.top < top + EDGE || u.top > bottom - 2 * EDGE) d = u.top - (top + EDGE); }
      else if (u.top < top + EDGE) d = u.top - (top + EDGE);
      else if (u.bottom > bottom - EDGE) d = u.bottom - (bottom - EDGE);
    }
    const max = document.documentElement.scrollHeight - window.innerHeight;
    const y = Math.max(0, Math.min(max, Math.round(window.scrollY + d)));
    if (Math.abs(y - window.scrollY) < 2) return;
    const onScreen = r.top >= top && r.bottom <= bottom;
    window.scrollTo({ top: y, behavior: reduce.matches || entry || !onScreen ? 'auto' : 'smooth' });
  });
})();


/* KCH 22 Oct 2026: phone menu "load more on scroll" (header > Mega menu tab block > "Phone menu:
   load more products on scroll", one tick per tab). snippets/kch-drawer-tab-panel.liquid prints a
   hidden [data-kch-menu-more] marker only when that tick is on and the collection holds more than the
   tab's limit. Nothing is fetched on page load or on menu open. The rest of the collection (up to
   24, sections/kch-menu-more.liquid) is fetched once, when:
     - the shopper scrolls the list to within 120px of its end, or
     - keyboard focus reaches the last product row (the keyboard and screen reader route; focus
       stays where it is and the next Tab continues into the new rows), or
     - the tab opens with a list too short to scroll, which would otherwise never load.
   No visible button (Sean 22 Oct). Below 768px only. */
(() => {
  const mq = window.matchMedia('(max-width: 767px)');
  const busy = new WeakSet();
  const root = () => (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  async function load(box) {
    if (!mq.matches || busy.has(box) || !box.isConnected) return;
    const list = box.parentElement && box.parentElement.querySelector('.horizontal-products');
    if (!list) return;
    busy.add(box);
    list.setAttribute('aria-busy', 'true');
    try {
      const res = await fetch(`${root()}collections/${encodeURIComponent(box.dataset.handle)}?section_id=kch-menu-more`, { credentials: 'same-origin' });
      if (!res.ok) throw new Error(String(res.status));
      const doc = new DOMParser().parseFromString(await res.text(), 'text/html');
      const wrap = doc.querySelector('[data-kch-menu-more-rows]');
      const rows = wrap ? [...wrap.children].slice(parseInt(box.dataset.offset, 10) || 0) : [];
      rows.forEach((row) => list.appendChild(document.importNode(row, true)));
      list.removeAttribute('aria-busy');
      box.remove();
    } catch (e) {
      /* A later scroll or focus tries again; View all is always below. */
      busy.delete(box);
      list.removeAttribute('aria-busy');
    }
  }
  const boxFor = (el) => {
    const sc = el.closest && el.closest('.drawer__scrollable');
    return sc ? sc.querySelector(':scope > [data-kch-menu-more]') : null;
  };
  document.addEventListener('scroll', (e) => {
    const sc = e.target;
    if (!(sc instanceof Element) || !sc.classList.contains('drawer__scrollable') || sc.scrollTop <= 0) return;
    const box = sc.querySelector(':scope > [data-kch-menu-more]');
    if (box && sc.scrollTop + sc.clientHeight >= sc.scrollHeight - 120) load(box);
  }, { capture: true, passive: true });
  document.addEventListener('focusin', (e) => {
    const row = e.target.closest && e.target.closest('.horizontal-products > .horizontal-product');
    if (!row || row.nextElementSibling) return;
    const box = boxFor(row);
    if (box) load(box);
  });
  document.addEventListener('toggle', (e) => {
    if (!e.target.open) return;
    setTimeout(() => e.target.querySelectorAll('[data-kch-menu-more]').forEach((box) => {
      const sc = box.closest('.drawer__scrollable');
      if (sc && sc.offsetParent !== null && sc.scrollHeight <= sc.clientHeight + 1) load(box);
    }), 450);
  }, true);
})();
/* KCH 22 Oct 2026: fitStickyNote(). Keeps the sticky-bar price note (p.kch-sticky-note[data-kch-fit], main-product.liquid)
   on one line. CSS sets the starting size (phones: English 13px, other languages 15px; desktop 12px) and nowrap.
   If the text is wider than its row less 12px of breathing room, step down 0.5px at a time to a 10px floor. Re-runs when the row width changes
   (rotation, resize) through a ResizeObserver. Touches only the note's inline font-size. */
(() => {
  const fit = (n) => {
    n.style.fontSize = '';
    if (!n.clientWidth) return;
    let px = parseFloat(getComputedStyle(n).fontSize);
    const r = document.createRange();
    r.selectNodeContents(n);
    const room = n.clientWidth - 12;
    while (r.getBoundingClientRect().width > room && px > 10) {
      px -= 0.5;
      n.style.fontSize = px + 'px';
    }
  };
  const init = () => {
    document.querySelectorAll('.kch-sticky-note[data-kch-fit]').forEach((n) => {
      if (n.dataset.kchFitOn) return;
      n.dataset.kchFitOn = '1';
      fit(n);
      if ('ResizeObserver' in window) {
        let w = n.clientWidth;
        new ResizeObserver(() => { if (n.clientWidth !== w) { w = n.clientWidth; fit(n); } }).observe(n);
      }
    });
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init); else init();
  if (document.fonts && document.fonts.ready) document.fonts.ready.then(() => document.querySelectorAll('.kch-sticky-note[data-kch-fit]').forEach(fit));
})();
